import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supplierReconSessionsAPI, supplierReconAPI, suppliersAPI, reconShareAPI } from '../lib/api';
import useAuthStore from '../store/authStore';
import SupplierReconReport from '../components/SupplierReconReport';
import SupplierReconAccounting from '../components/SupplierReconAccounting';
import EmailDetailModal from '../components/EmailDetailModal';
import FilePreviewModal from '../components/FilePreviewModal';
import {
  FiArrowLeft, FiPackage, FiLoader, FiAlertCircle, FiRefreshCw,
  FiCheckCircle, FiXCircle, FiClock, FiFileText, FiUsers,
  FiPlay, FiCloud, FiMail, FiDatabase, FiDownload, FiExternalLink, FiAlertTriangle, FiEye,
  FiThumbsUp, FiThumbsDown, FiEdit2, FiShare2, FiCopy, FiTrash2, FiCheck, FiUpload, FiX, FiUnlock,
} from 'react-icons/fi';

const STATUS_CONFIG = {
  pending: { icon: FiClock, color: 'text-gray-500', bg: 'bg-gray-100', label: 'Pending' },
  syncing: { icon: FiRefreshCw, color: 'text-blue-600', bg: 'bg-blue-100', label: 'Syncing', spin: true },
  processing: { icon: FiLoader, color: 'text-purple-600', bg: 'bg-purple-100', label: 'Processing AI', spin: true },
  matching: { icon: FiLoader, color: 'text-amber-600', bg: 'bg-amber-100', label: 'Matching', spin: true },
  in_progress: { icon: FiClock, color: 'text-amber-600', bg: 'bg-amber-100', label: 'In Progress' },
  complete: { icon: FiCheckCircle, color: 'text-green-600', bg: 'bg-green-100', label: 'Complete' },
  error: { icon: FiXCircle, color: 'text-red-600', bg: 'bg-red-100', label: 'Error' },
};

const AI_STATUS_CONFIG = {
  pending: { color: 'text-gray-400', label: 'Pending' },
  processing: { color: 'text-purple-600', label: 'Processing' },
  completed: { color: 'text-green-600', label: 'Done' },
  error: { color: 'text-red-600', label: 'Error' },
};

const DOC_TYPE_LABELS = {
  supplier_invoices: 'Invoice',
  supplier_statements: 'Statement',
  company_supplier_ledgers: 'Ledger',
  supplier_payments: 'Payment',
  payment_files: 'Payment File',
  purchase_listing: 'Purchase Listings',
  supplier_payout: 'Payouts',
  pos_cashup: 'Cashups',
  pos_day_end: 'Day End Reports',
  purchase_order: 'Purchase Order',
};

const TYPE_SHORT = {
  supplier_invoices: 'Inv',
  supplier_statements: 'Stmt',
  company_supplier_ledgers: 'Ledger',
  supplier_payments: 'Paid',
  payment_files: 'PF',
  purchase_listing: 'PL',
  supplier_payout: 'Payout',
  pos_cashup: 'Cashup',
  pos_day_end: 'DayEnd',
  purchase_order: 'PO',
};

const FINANCIAL_ROLES = {
  supplier_invoices: 'primary',
  supplier_statements: 'secondary',
  company_supplier_ledgers: 'ledger',
  supplier_payments: 'payments',
  payment_files: 'payments',
  purchase_listing: 'primary',
  supplier_payout: 'payments',
  pos_cashup: 'secondary',
  pos_day_end: 'secondary',
  purchase_order: 'primary',
};

const DEFAULT_ORDERED_TYPES = ['supplier_invoices', 'supplier_statements', 'company_supplier_ledgers', 'supplier_payments', 'payment_files'];

function fmt(amount, currency = 'ZAR') {
  if (amount == null) return '—';
  const safeCurrency = (currency && typeof currency === 'string' && currency.trim().length === 3) ? currency.trim() : 'ZAR';
  return new Intl.NumberFormat('en-ZA', { style: 'currency', currency: safeCurrency }).format(amount);
}

function formatDate(str) {
  if (!str) return '—';
  return new Date(str).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' });
}

// Same meaning as the runs list: a finished run only reads "Complete" once it has been accepted
function getDisplayStatus(status, reconAccepted) {
  return status === 'complete' && !reconAccepted ? 'in_progress' : status;
}

function StatusBadge({ status, reconAccepted }) {
  const cfg = STATUS_CONFIG[getDisplayStatus(status, reconAccepted)] || STATUS_CONFIG.pending;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${cfg.bg} ${cfg.color}`}>
      <Icon className={`w-4 h-4 ${cfg.spin ? 'animate-spin' : ''}`} />
      {cfg.label}
    </span>
  );
}

function ProgressStep({ label, active, done }) {
  return (
    <div className={`flex items-center gap-2 text-sm ${done ? 'text-green-600' : active ? 'text-blue-600' : 'text-gray-400'}`}>
      {done ? <FiCheckCircle className="w-4 h-4" /> : active ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiClock className="w-4 h-4" />}
      {label}
    </div>
  );
}

const DOC_TYPE_OPTIONS = [
  { value: 'supplier_invoices', label: 'Invoice' },
  { value: 'supplier_statements', label: 'Statement' },
  { value: 'company_supplier_ledgers', label: 'Ledger' },
  { value: 'supplier_payments', label: 'Payment' },
  { value: 'payment_files', label: 'Payment File' },
  { value: 'purchase_listing', label: 'Purchase Listings' },
  { value: 'supplier_payout', label: 'Payouts' },
  { value: 'pos_cashup', label: 'Cashups' },
  { value: 'pos_day_end', label: 'Day End Reports' },
  { value: 'purchase_order', label: 'Purchase Order' },
];

function DocumentsTab({ documents: initialDocs, exchangeMappings, matches, onReprocessed, onRerunMatching, customDocTypes = [], sessionId, reconAccepted = false, onReopenRecon, reopening = false, onAcceptedConflict }) {
  const [docs, setDocs] = useState(initialDocs);
  const [emailPreview, setEmailPreview] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [reprocessing, setReprocessing] = useState({});
  const [reprocessingAll, setReprocessingAll] = useState(false);
  const [saving, setSaving] = useState({});
  const [uploadDragging, setUploadDragging] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  // editingNote[docId] = note text being typed before submission
  const [editingNote, setEditingNote] = useState({});
  // editingAmount[docId] = value being edited
  const [editingAmount, setEditingAmount] = useState({});
  // editingSupplier[docId] = supplier name text being edited
  const [editingSupplier, setEditingSupplier] = useState({});
  // After a supplier rename, prompt to re-run matching
  const [supplierChangedFlag, setSupplierChangedFlag] = useState(false);

  // Build dynamic doc type options/labels that include custom types for this group
  const allDocTypeOptions = useMemo(() => [
    ...DOC_TYPE_OPTIONS,
    ...(customDocTypes || []).filter(t => t.name?.trim()).map(t => ({ value: t.name.trim(), label: t.name.trim() })),
  ], [customDocTypes]);

  const allDocTypeLabels = useMemo(() => {
    const labels = { ...DOC_TYPE_LABELS };
    (customDocTypes || []).forEach(t => { if (t.name?.trim()) labels[t.name.trim()] = t.name.trim(); });
    return labels;
  }, [customDocTypes]);

  // Collect all known supplier names from documents + matches
  const knownSuppliers = [...new Set([
    ...initialDocs.map(d => d.user_supplier_name || d.supplier_name).filter(Boolean),
    ...(matches || []).map(m => m.supplier_name).filter(Boolean),
  ])].sort();

  // Build cross-reference map: for each invoice, check if its ref appears in statement/ledger raw text
  // Returns: { [docId]: { onStatement: bool, onLedger: bool } }
  const crossRefMap = useMemo(() => {
    const map = {};
    // Group docs by effective supplier
    const supplierDocs = {};
    for (const d of docs) {
      const name = d.user_supplier_name || d.supplier_name || 'unknown';
      if (!supplierDocs[name]) supplierDocs[name] = [];
      supplierDocs[name].push(d);
    }
    for (const group of Object.values(supplierDocs)) {
      const effectiveType = (d) => d.user_doc_type || d.detected_doc_type || d.document_type;
      const invoices = group.filter(d => effectiveType(d) === 'supplier_invoices');
      const statements = group.filter(d => effectiveType(d) === 'supplier_statements');
      const ledgers = group.filter(d => effectiveType(d) === 'company_supplier_ledgers');
      if (invoices.length === 0) continue;
      // Build sets of invoice refs from statements and ledgers using ai_raw.invoice_refs
      const stmtRefs = statements.flatMap(d =>
        (d.ai_raw?.invoice_refs || []).map(r => r.toLowerCase().trim())
      );
      const ledgerRefs = ledgers.flatMap(d =>
        (d.ai_raw?.invoice_refs || []).map(r => r.toLowerCase().trim())
      );
      // Fuzzy ref match: exact OR substring (handles SAP full doc numbers like 5105029292 vs 029292)
      const refMatches = (invRef, refList) => refList.some(r => r === invRef || r.includes(invRef) || invRef.includes(r));
      // Check if ledgers/statements have been re-processed (have invoice_refs populated)
      const ledgerHasRefs = ledgers.some(d => (d.ai_raw?.invoice_refs || []).length > 0);
      const stmtHasRefs = statements.some(d => (d.ai_raw?.invoice_refs || []).length > 0);
      for (const inv of invoices) {
        const ref = inv.supplier_ref?.trim();
        if (!ref) continue;
        const refLower = ref.toLowerCase();
        const aiOnStmt = stmtHasRefs ? refMatches(refLower, stmtRefs) : false;
        const aiOnLedger = ledgerHasRefs ? refMatches(refLower, ledgerRefs) : false;
        map[inv.id] = {
          // user override wins; null = no override
          onStatement: inv.user_on_statement !== null && inv.user_on_statement !== undefined ? inv.user_on_statement : aiOnStmt,
          onLedger: inv.user_on_ledger !== null && inv.user_on_ledger !== undefined ? inv.user_on_ledger : aiOnLedger,
          userOverrideStmt: inv.user_on_statement !== null && inv.user_on_statement !== undefined,
          userOverrideLedger: inv.user_on_ledger !== null && inv.user_on_ledger !== undefined,
          hasStatement: statements.length > 0,
          hasLedger: ledgers.length > 0,
          stmtNeedsReprocess: statements.length > 0 && !stmtHasRefs,
          ledgerNeedsReprocess: ledgers.length > 0 && !ledgerHasRefs,
        };
      }
    }
    return map;
  }, [docs]);

  // Sync if parent refreshes
  useEffect(() => { setDocs(initialDocs); }, [initialDocs]);

  const patchDoc = async (docId, updates) => {
    setSaving(prev => ({ ...prev, [docId]: true }));
    // Optimistic update
    setDocs(prev => prev.map(d => d.id === docId ? { ...d, ...updates } : d));
    try {
      const res = await supplierReconSessionsAPI.updateDocument(docId, updates);
      setDocs(prev => prev.map(d => d.id === docId ? { ...d, ...res.data } : d));
    } catch (err) {
      console.error('Doc patch error:', err);
      // Revert on error
      setDocs(initialDocs);
    } finally {
      setSaving(prev => ({ ...prev, [docId]: false }));
    }
  };

  const handleAccept = (doc) => patchDoc(doc.id, { review_status: 'accepted', review_note: doc.review_note || null });

  const handleReject = (doc) => {
    if (editingNote[doc.id] === undefined) {
      // Open note input first
      setEditingNote(prev => ({ ...prev, [doc.id]: doc.review_note || '' }));
    } else {
      // Submit rejection with note
      patchDoc(doc.id, { review_status: 'rejected', review_note: editingNote[doc.id] || null });
      setEditingNote(prev => { const n = { ...prev }; delete n[doc.id]; return n; });
    }
  };

  const handleTypeChange = (doc, newType) => patchDoc(doc.id, { user_doc_type: newType || null });

  const handleAmountEdit = (doc) => {
    setEditingAmount(prev => ({ ...prev, [doc.id]: String(doc.user_amount ?? doc.amount ?? '') }));
  };

  const handleAmountSave = (doc) => {
    const val = editingAmount[doc.id];
    const parsed = val === '' ? null : parseFloat(val);
    const newAmount = isNaN(parsed) ? null : parsed;
    const originalAmount = doc.user_amount ?? doc.amount ?? null;
    // Only save if value actually changed — avoid accidentally locking in user_amount on mere click
    if (newAmount !== originalAmount) {
      patchDoc(doc.id, { user_amount: newAmount });
    }
    setEditingAmount(prev => { const n = { ...prev }; delete n[doc.id]; return n; });
  };

  const handleSupplierEdit = (doc) => {
    setEditingSupplier(prev => ({ ...prev, [doc.id]: doc.user_supplier_name || doc.supplier_name || '' }));
  };

  const handleSupplierSave = (doc) => {
    const val = (editingSupplier[doc.id] || '').trim();
    const newName = val === '' ? null : val;
    patchDoc(doc.id, { user_supplier_name: newName });
    setEditingSupplier(prev => { const n = { ...prev }; delete n[doc.id]; return n; });
    setSupplierChangedFlag(true);
  };

  const handleSupplierCancel = (docId) => {
    setEditingSupplier(prev => { const n = { ...prev }; delete n[docId]; return n; });
  };

  const handleReprocess = async (doc) => {
    setReprocessing(prev => ({ ...prev, [doc.id]: true }));
    try {
      await supplierReconSessionsAPI.reprocessDocument(doc.id);
      // Large documents (e.g. multi-page purchase listings) process in server-side page
      // batches — one batch per /process call. Drive the remaining batches here and poll
      // this specific document's ai_status, instead of leaving it to stall until the user
      // manually refreshes or clicks Re-process again for each batch.
      let guard = 0;
      let consecutiveErrors = 0;
      let docStatus = 'processing';
      while ((docStatus === 'processing' || docStatus === 'pending') && guard < 200) {
        try {
          await supplierReconSessionsAPI.processSession(sessionId);
          const sessRes = await supplierReconSessionsAPI.getSession(sessionId);
          const updatedDoc = (sessRes.data?.documents || []).find(d => d.id === doc.id);
          docStatus = updatedDoc?.ai_status || 'completed';
          consecutiveErrors = 0;
        } catch (pollErr) {
          // Transient errors (e.g. a momentary DB timeout) shouldn't abandon a large
          // document mid-batch — retry a few times with backoff before giving up.
          consecutiveErrors++;
          console.error('Reprocess poll error:', pollErr?.response?.data?.error || pollErr?.message);
          if (consecutiveErrors >= 5) break;
          await new Promise(r => setTimeout(r, 2000 * consecutiveErrors));
        }
        guard++;
      }
      if (onReprocessed) onReprocessed();
    } catch (err) {
      console.error('Reprocess error:', err);
    } finally {
      setReprocessing(prev => ({ ...prev, [doc.id]: false }));
    }
  };

  const handleReprocessAll = async () => {
    if (!sessionId) return;
    setReprocessingAll(true);
    try {
      // Reset all eligible docs to pending and set session status back to 'processing'
      // The existing processLoop effect will pick them up one at a time
      await supplierReconSessionsAPI.reprocessAll(sessionId);
      if (onReprocessed) onReprocessed();
    } catch (err) {
      // Server refuses on an accepted recon (409): the parent shows its message and refreshes
      if (onAcceptedConflict && onAcceptedConflict(err)) return;
      console.error('Reprocess all error:', err);
    } finally {
      setReprocessingAll(false);
    }
  };

  const handleUploadSelect = (e) => {
    const selected = Array.from(e.target.files || []).filter(f => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'));
    setUploadFiles(prev => {
      const existingNames = new Set(prev.map(f => f.name));
      return [...prev, ...selected.filter(f => !existingNames.has(f.name))];
    });
    setUploadError(null);
    e.target.value = '';
  };

  const handleUploadDrop = (e) => {
    e.preventDefault();
    setUploadDragging(false);
    const dropped = Array.from(e.dataTransfer.files).filter(f => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'));
    if (dropped.length === 0) { setUploadError('Only PDF files are allowed.'); return; }
    setUploadFiles(prev => {
      const existingNames = new Set(prev.map(f => f.name));
      return [...prev, ...dropped.filter(f => !existingNames.has(f.name))];
    });
    setUploadError(null);
  };

  const handleUploadSubmit = async () => {
    if (!uploadFiles.length || !sessionId) return;
    setUploading(true);
    setUploadError(null);
    try {
      const res = await supplierReconSessionsAPI.uploadDocuments(sessionId, uploadFiles);
      const newDocs = res.data?.documents || [];
      setDocs(prev => [...newDocs, ...prev]);
      setUploadFiles([]);
      // Drive AI processing in background — the parent processLoop requires session.status==='processing'
      // which may race on read-after-write. Run /process calls here until all pending docs are done,
      // then call onReprocessed once to refresh the UI. Do NOT call onReprocessed mid-loop as that
      // triggers loadSession which can activate the parent processLoop and kick off run-matching.
      (async () => {
        let remaining = newDocs.length;
        while (remaining > 0) {
          try {
            const procRes = await supplierReconSessionsAPI.processSession(sessionId);
            remaining = procRes.data?.remaining ?? 0;
          } catch {
            break;
          }
        }
        // Match any newly-processed suppliers WITHOUT resetting existing matches.
        // run-matching (no reset=true) only fills in suppliers that don't yet have a
        // match row — it never touches existing matches, so accepted/rejected review
        // decisions on other suppliers are left completely untouched.
        try {
          let matchRemaining = 1;
          let guard = 0;
          while (matchRemaining > 0 && guard < 200 && !reconAccepted) {
            const matchRes = await supplierReconSessionsAPI.runMatching(sessionId);
            matchRemaining = matchRes.data?.remaining ?? 0;
            guard++;
          }
        } catch {
          // Non-fatal — user can still use "Re-run Matching" manually if this fails
        }
        if (onReprocessed) onReprocessed();
      })();
    } catch (err) {
      setUploadError(err.response?.data?.error || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleView = (doc) => {
    if (doc.source === 'exchange') {
      const mailboxId = doc.ai_raw?.mailbox_id || (exchangeMappings && exchangeMappings[0]?.mailbox_id);
      if (mailboxId && doc.external_id) {
        setEmailPreview({ mailboxId, messageId: doc.external_id, subject: doc.file_name });
      } else if (doc.external_url) {
        window.open(doc.external_url, '_blank', 'noopener noreferrer');
      }
    } else {
      setFilePreview(doc);
    }
  };

  const DOC_SORT_ORDER = { supplier_invoices: 0, purchase_listing: 1, supplier_statements: 2, company_supplier_ledgers: 3, supplier_payments: 4, payment_files: 5, supplier_payout: 6, pos_cashup: 7, pos_day_end: 8 };
  const sortedDocs = [...docs].sort((a, b) => {
    const aUp = a.source === 'doxolve' ? 0 : 1;
    const bUp = b.source === 'doxolve' ? 0 : 1;
    if (aUp !== bUp) return aUp - bUp;
    const aType = a.user_doc_type || a.detected_doc_type || a.document_type;
    const bType = b.user_doc_type || b.detected_doc_type || b.document_type;
    const aIdx = DOC_SORT_ORDER[aType] ?? 99;
    const bIdx = DOC_SORT_ORDER[bType] ?? 99;
    if (aIdx !== bIdx) return aIdx - bIdx;
    return (b.period_date || '').localeCompare(a.period_date || '');
  });
  const uploadedCount = sortedDocs.filter(d => d.source === 'doxolve').length;
  const docListItems = sortedDocs.map((doc, idx) => {
    const aiCfg = AI_STATUS_CONFIG[doc.ai_status] || AI_STATUS_CONFIG.pending;
    const isRejected = doc.review_status === 'rejected';
    const isAccepted = doc.review_status === 'accepted';
    const effectiveType = doc.user_doc_type || doc.detected_doc_type || doc.document_type;
    const effectiveAmount = doc.user_amount != null ? doc.user_amount : doc.amount;
    const isSaving = saving[doc.id];
    const isEditingNote = editingNote[doc.id] !== undefined;
    const isEditingAmount = editingAmount[doc.id] !== undefined;
    const crossRef = effectiveType === 'supplier_invoices' ? crossRefMap[doc.id] : null;
    const showUploadedHeader = idx === 0 && uploadedCount > 0;
    const showSyncedHeader = idx === uploadedCount && uploadedCount > 0 && sortedDocs.length > uploadedCount;
    return (
      <React.Fragment key={doc.id}>
        {showUploadedHeader && (
          <div className="flex items-center gap-2 pt-1 pb-0.5">
            <span className="text-xs font-semibold text-blue-600 uppercase tracking-wide">Manually Uploaded</span>
            <div className="flex-1 h-px bg-blue-100" />
          </div>
        )}
        {showSyncedHeader && (
          <div className="flex items-center gap-2 pt-2 pb-0.5">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Synced from SharePoint / Email</span>
            <div className="flex-1 h-px bg-gray-100" />
          </div>
        )}
        <div className={`bg-white border rounded-xl p-4 transition-colors ${
          isRejected ? 'border-red-200 bg-red-50/30 opacity-70' : isAccepted ? 'border-green-200' : 'border-gray-200 hover:border-gray-300'
        }`}>
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex-shrink-0">
              {doc.source === 'sharepoint'
                ? <FiCloud className="w-4 h-4 text-blue-400" />
                : doc.source === 'doxolve'
                  ? <FiDatabase className="w-4 h-4 text-green-500" />
                  : <FiMail className="w-4 h-4 text-purple-400" />
              }
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className={`text-sm font-medium truncate ${isRejected ? 'text-gray-400 line-through' : 'text-gray-900'}`} title={doc.file_name}>
                      {doc.file_name}
                    </p>
                    {isAccepted && <span className="text-xs text-green-600 font-medium">✓ Accepted</span>}
                    {isRejected && <span className="text-xs text-red-500 font-medium">✗ Rejected</span>}
                  </div>
                  <div className="flex items-center gap-3 mt-1 flex-wrap">
                    {doc.ai_status === 'completed' ? (
                      <select
                        value={effectiveType}
                        onChange={e => handleTypeChange(doc, e.target.value)}
                        disabled={isSaving}
                        title="Override document type"
                        className={`text-xs border rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-green-400 disabled:opacity-50 ${
                          doc.user_doc_type ? 'border-green-300 text-green-700 bg-green-50' : 'border-gray-200 text-gray-500 bg-white'
                        }`}
                      >
                        {allDocTypeOptions.map(o => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    ) : (
                      <span className="text-xs text-gray-500">{allDocTypeLabels[effectiveType] || effectiveType}</span>
                    )}
                    {doc.user_doc_type && <span className="text-xs text-green-600 font-medium">type overridden</span>}
                    {effectiveType !== 'purchase_listing' && (editingSupplier[doc.id] !== undefined ? (
                      <span className="flex items-center gap-1">
                        <input
                          list={`suppliers-${doc.id}`}
                          value={editingSupplier[doc.id]}
                          onChange={e => setEditingSupplier(prev => ({ ...prev, [doc.id]: e.target.value }))}
                          onKeyDown={e => { if (e.key === 'Enter') handleSupplierSave(doc); if (e.key === 'Escape') handleSupplierCancel(doc.id); }}
                          onBlur={() => handleSupplierSave(doc)}
                          placeholder="Supplier name..."
                          className="text-xs border border-blue-400 rounded px-1.5 py-0.5 w-44 focus:outline-none"
                          autoFocus
                        />
                        <datalist id={`suppliers-${doc.id}`}>
                          {knownSuppliers.map(s => <option key={s} value={s} />)}
                        </datalist>
                        <button onMouseDown={e => { e.preventDefault(); handleSupplierCancel(doc.id); }} className="text-xs text-gray-400 hover:text-gray-600">✕</button>
                      </span>
                    ) : (
                      <button
                        onClick={() => handleSupplierEdit(doc)}
                        className={`text-xs font-medium flex items-center gap-0.5 hover:underline ${
                          doc.user_supplier_name ? 'text-blue-700' : doc.supplier_name ? 'text-gray-700' : 'text-gray-300'
                        }`}
                        title="Click to reassign supplier"
                      >
                        {doc.user_supplier_name || doc.supplier_name || <span className="italic">no supplier</span>}
                        <FiEdit2 className="w-3 h-3 text-gray-400 ml-0.5" />
                      </button>
                    ))}
                    {effectiveType !== 'purchase_listing' && doc.user_supplier_name && <span className="text-xs text-blue-600 font-medium">supplier overridden</span>}
                    {doc.supplier_ref && (
                      <span className="text-xs text-gray-400">Ref: {doc.supplier_ref}</span>
                    )}
                    {crossRef && doc.supplier_ref && (
                      <>
                        {crossRef.hasStatement && (
                          <span className="inline-flex items-center gap-1">
                            <span
                              className={`text-xs px-1.5 py-0.5 rounded font-medium ${crossRef.stmtNeedsReprocess ? 'bg-gray-100 text-gray-400' : crossRef.onStatement ? 'bg-green-100 text-green-700' : 'bg-red-50 text-red-500'}`}
                              title={crossRef.stmtNeedsReprocess ? 'Re-process statement to enable cross-reference check' : crossRef.onStatement ? `Invoice ref found in statement${crossRef.userOverrideStmt ? ' (manually confirmed)' : ''}` : 'Invoice ref NOT found in statement'}
                            >
                              Stmt {crossRef.stmtNeedsReprocess ? '?' : crossRef.onStatement ? '✓' : '✗'}
                            </span>
                            {!crossRef.stmtNeedsReprocess && !crossRef.onStatement && (
                              <button
                                onClick={() => patchDoc(doc.id, { user_on_statement: true })}
                                className="text-xs px-1 py-0.5 rounded border border-green-400 text-green-600 hover:bg-green-50"
                                title="Manually confirm this ref is on the statement"
                              >
                                Confirm
                              </button>
                            )}
                            {crossRef.userOverrideStmt && crossRef.onStatement && (
                              <button
                                onClick={() => patchDoc(doc.id, { user_on_statement: null })}
                                className="text-xs text-gray-400 hover:text-red-400"
                                title="Remove manual confirmation"
                              >
                                undo
                              </button>
                            )}
                          </span>
                        )}
                        {crossRef.hasLedger && (
                          <span className="inline-flex items-center gap-1">
                            <span
                              className={`text-xs px-1.5 py-0.5 rounded font-medium ${crossRef.ledgerNeedsReprocess ? 'bg-gray-100 text-gray-400' : crossRef.onLedger ? 'bg-green-100 text-green-700' : 'bg-red-50 text-red-500'}`}
                              title={crossRef.ledgerNeedsReprocess ? 'Re-process ledger to enable cross-reference check' : crossRef.onLedger ? `Invoice ref found in ledger${crossRef.userOverrideLedger ? ' (manually confirmed)' : ''}` : 'Invoice ref NOT found in ledger'}
                            >
                              Ledger {crossRef.ledgerNeedsReprocess ? '?' : crossRef.onLedger ? '✓' : '✗'}
                            </span>
                            {!crossRef.ledgerNeedsReprocess && !crossRef.onLedger && (
                              <button
                                onClick={() => patchDoc(doc.id, { user_on_ledger: true })}
                                className="text-xs px-1 py-0.5 rounded border border-green-400 text-green-600 hover:bg-green-50"
                                title="Manually confirm this ref is on the ledger"
                              >
                                Confirm
                              </button>
                            )}
                            {crossRef.userOverrideLedger && crossRef.onLedger && (
                              <button
                                onClick={() => patchDoc(doc.id, { user_on_ledger: null })}
                                className="text-xs text-gray-400 hover:text-red-400"
                                title="Remove manual confirmation"
                              >
                                undo
                              </button>
                            )}
                          </span>
                        )}
                      </>
                    )}
                    {isEditingAmount ? (
                      <span className="flex items-center gap-1">
                        <input
                          type="number"
                          step="0.01"
                          value={editingAmount[doc.id]}
                          onChange={e => setEditingAmount(prev => ({ ...prev, [doc.id]: e.target.value }))}
                          onBlur={() => handleAmountSave(doc)}
                          onKeyDown={e => { if (e.key === 'Enter') handleAmountSave(doc); if (e.key === 'Escape') setEditingAmount(prev => { const n = {...prev}; delete n[doc.id]; return n; }); }}
                          className="text-xs border border-green-400 rounded px-1 py-0.5 w-28 focus:outline-none"
                          autoFocus
                        />
                      </span>
                    ) : (
                      <button
                        onClick={() => handleAmountEdit(doc)}
                        className={`text-xs font-medium flex items-center gap-0.5 hover:underline ${
                          doc.user_amount != null ? 'text-green-700' : effectiveAmount != null ? 'text-green-700' : 'text-gray-400'
                        }`}
                        title="Click to override amount"
                      >
                        {effectiveAmount != null ? fmt(effectiveAmount, doc.currency || 'ZAR') : <span className="text-gray-300">no amount</span>}
                        <FiEdit2 className="w-3 h-3 text-gray-400 ml-0.5" />
                      </button>
                    )}
                    {doc.user_amount != null && <span className="text-xs text-green-600 font-medium">amount overridden</span>}
                    {doc.period_date && (
                      <span className="text-xs text-gray-400">{formatDate(doc.period_date)}</span>
                    )}
                  </div>
                  {isRejected && doc.review_note && (
                    <p className="mt-1 text-xs text-red-500 italic">Note: {doc.review_note}</p>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
                  <button
                    onClick={() => handleAccept(doc)}
                    disabled={isSaving || isAccepted}
                    className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-lg border transition-colors disabled:opacity-40 ${
                      isAccepted ? 'bg-green-100 text-green-700 border-green-300' : 'bg-white text-green-600 border-green-200 hover:bg-green-50'
                    }`}
                    title="Accept this document for matching"
                  >
                    <FiThumbsUp className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => handleReject(doc)}
                    disabled={isSaving}
                    className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-lg border transition-colors disabled:opacity-40 ${
                      isRejected ? 'bg-red-100 text-red-600 border-red-300' : 'bg-white text-red-500 border-red-200 hover:bg-red-50'
                    }`}
                    title={isEditingNote ? 'Confirm rejection' : 'Reject this document'}
                  >
                    <FiThumbsDown className="w-3.5 h-3.5" />
                    {isEditingNote && <span>Confirm</span>}
                  </button>
                  <span className={`text-xs font-medium ${aiCfg.color}`}>{aiCfg.label}</span>
                  <button
                    onClick={() => handleReprocess(doc)}
                    disabled={reprocessing[doc.id] || doc.ai_status === 'processing'}
                    className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-amber-50 text-amber-700 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors disabled:opacity-50"
                    title="Re-run AI extraction on this document"
                  >
                    {reprocessing[doc.id]
                      ? <FiLoader className="w-3.5 h-3.5 animate-spin" />
                      : <FiRefreshCw className="w-3.5 h-3.5" />
                    }
                    Re-process
                  </button>
                  <button
                    onClick={() => handleView(doc)}
                    className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
                    title={doc.source === 'exchange' ? 'View email' : 'Preview file'}
                  >
                    <FiEye className="w-3.5 h-3.5" />
                    View
                  </button>
                </div>
              </div>
              {isEditingNote && (
                <div className="mt-2 flex items-start gap-2">
                  <textarea
                    value={editingNote[doc.id]}
                    onChange={e => setEditingNote(prev => ({ ...prev, [doc.id]: e.target.value }))}
                    placeholder="Reason for rejection (optional)..."
                    rows={2}
                    className="flex-1 text-xs border border-red-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-red-400 resize-none"
                  />
                  <button
                    onClick={() => setEditingNote(prev => { const n = {...prev}; delete n[doc.id]; return n; })}
                    className="text-xs text-gray-400 hover:text-gray-600 mt-1"
                  >Cancel</button>
                </div>
              )}
              {doc.type_mismatch && doc.detected_doc_type && !doc.user_doc_type && (
                <div className="mt-2 flex items-start gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                  <FiAlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  <span>
                    File appears to be a <strong>{doc.detected_doc_type.replace(/_/g, ' ')}</strong> but is in the <strong>{allDocTypeLabels[doc.document_type] || doc.document_type}</strong> folder.
                  </span>
                </div>
              )}
              {doc.ai_status === 'error' && (doc.ai_error || doc.ai_raw?.last_error) && (
                <div className="mt-2 flex items-start gap-1.5 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5">
                  <FiAlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  <span>Extraction failed: {doc.ai_error || doc.ai_raw?.last_error}</span>
                </div>
              )}
              {doc.ai_summary && (
                <p className="mt-2 text-xs text-gray-500 italic border-t border-gray-100 pt-2">
                  {doc.ai_summary}
                </p>
              )}
            </div>
          </div>
        </div>
      </React.Fragment>
    );
  });

  return (
    <>
      {/* Upload Zone */}
      <div className="mb-4">
        <div
          onDragOver={(e) => { e.preventDefault(); setUploadDragging(true); }}
          onDragLeave={() => setUploadDragging(false)}
          onDrop={handleUploadDrop}
          className={`border-2 border-dashed rounded-lg px-4 py-4 transition-colors ${uploadDragging ? 'border-blue-400 bg-blue-50' : 'border-gray-200 bg-gray-50 hover:border-blue-300 hover:bg-blue-50'}`}
        >
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <FiUpload className="w-4 h-4 text-blue-400 flex-shrink-0" />
              <span>{uploadDragging ? 'Drop PDFs here' : 'Drag & drop PDFs, or'}</span>
            </div>
            <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-white border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50 transition-colors">
              <FiUpload className="w-3.5 h-3.5" />
              Browse files
              <input type="file" accept=".pdf,application/pdf" multiple className="hidden" onChange={handleUploadSelect} />
            </label>
            {uploadFiles.length > 0 && (
              <button
                onClick={handleUploadSubmit}
                disabled={uploading}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {uploading ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : <FiUpload className="w-3.5 h-3.5" />}
                {uploading ? 'Uploading…' : `Upload ${uploadFiles.length} file${uploadFiles.length > 1 ? 's' : ''} & Process`}
              </button>
            )}
          </div>
          {uploadFiles.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {uploadFiles.map((f, i) => (
                <div key={i} className="inline-flex items-center gap-1.5 px-2 py-1 bg-white border border-gray-200 rounded text-xs text-gray-700">
                  <FiFileText className="w-3 h-3 text-red-400" />
                  {f.name}
                  <button onClick={() => setUploadFiles(prev => prev.filter((_, j) => j !== i))} className="ml-1 text-gray-400 hover:text-gray-600">
                    <FiX className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
          {uploadError && <p className="mt-2 text-xs text-red-600">{uploadError}</p>}
        </div>
      </div>

      {docs.length === 0 && (
        <div className="text-center py-12 text-gray-400 text-sm">No documents synced yet. Upload PDFs above to get started.</div>
      )}

      {docs.length > 0 && <>
      <div className="mb-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex-1">
          {supplierChangedFlag && reconAccepted && onReopenRecon && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
              <p className="text-sm text-amber-900">Supplier name changed. Reopen the recon to re-run matching.</p>
              <button
                type="button"
                onClick={onReopenRecon}
                disabled={reopening}
                className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-2 min-h-[44px] sm:min-h-0 sm:py-1.5 text-xs font-medium text-amber-900 bg-amber-100 border border-amber-300 rounded-lg hover:bg-amber-200 transition-colors disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-1"
              >
                <FiUnlock className="w-3.5 h-3.5" aria-hidden="true" />
                Reopen recon
              </button>
            </div>
          )}
          {supplierChangedFlag && onRerunMatching && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 flex items-center justify-between gap-3">
              <p className="text-sm text-blue-800">Supplier name changed — re-run matching to update the results.</p>
              <button
                onClick={() => { setSupplierChangedFlag(false); onRerunMatching(); }}
                className="flex-shrink-0 px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Re-run Matching
              </button>
            </div>
          )}
        </div>
        <div className="flex-shrink-0 flex items-center gap-2 flex-wrap justify-end">
          {reconAccepted && (
            <p id="reprocess-all-locked-hint" className="text-xs text-gray-600">Locked while the recon is accepted — reopen it first.</p>
          )}
          <button
            onClick={handleReprocessAll}
            disabled={reprocessingAll || reconAccepted}
            aria-describedby={reconAccepted ? 'reprocess-all-locked-hint' : undefined}
            className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-amber-50 text-amber-700 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-1"
            title={reconAccepted ? 'Reopen the recon to re-process documents' : 'Re-run AI extraction on all documents'}
          >
            {reprocessingAll
              ? <FiLoader className="w-3.5 h-3.5 animate-spin" />
              : <FiRefreshCw className="w-3.5 h-3.5" />
            }
            {reprocessingAll ? 'Re-processing…' : 'Re-process All'}
          </button>
        </div>
      </div>
      {emailPreview && (
        <EmailDetailModal
          mailboxId={emailPreview.mailboxId}
          messageId={emailPreview.messageId}
          subject={emailPreview.subject}
          onClose={() => setEmailPreview(null)}
        />
      )}
      {filePreview && (
        <FilePreviewModal
          doc={filePreview}
          onClose={() => setFilePreview(null)}
        />
      )}
    <div className="space-y-2">
      {docListItems}
    </div>
      </>}
    </>
  );
}

export default function SupplierReconSession() {
  const { id } = useParams();
  const navigate = useNavigate();
  const currentUserProfile = useAuthStore((state) => state.profile);
  const [session, setSession] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [matches, setMatches] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  // Accounting tab stays mounted (hidden) once opened so its filters, selection, data and running AI job survive tab switches
  const [accountingOpened, setAccountingOpened] = useState(false);
  const [accountingAiRunning, setAccountingAiRunning] = useState(false); // badge on the Accounting tab while AI matching runs
  useEffect(() => { if (activeTab === 'accounting') setAccountingOpened(true); }, [activeTab]);
  const [runningMatching, setRunningMatching] = useState(false);
  const [loadingReport, setLoadingReport] = useState(false);
  const [markingError, setMarkingError] = useState(false);
  const [exchangeMappings, setExchangeMappings] = useState([]);
  const [matchSaving, setMatchSaving] = useState({});
  const [matchEditingNote, setMatchEditingNote] = useState({});
  const [matchEditingName, setMatchEditingName] = useState({}); // matchId -> string being edited
  const [matchDiscount, setMatchDiscount] = useState({}); // matchId -> { mode: 'pct'|'value', input: string, locked?: number } | null
  const [plLineEdits, setPlLineEdits] = useState({}); // { [matchId]: { [lineIdx]: { ref?, amount? } } }
  const [plLineSaving, setPlLineSaving] = useState({}); // { [matchId]: bool }
  const [invAmountEdits, setInvAmountEdits] = useState({}); // { [matchId]: { [docId]: string } }
  const [invLineEdits, setInvLineEdits] = useState({}); // { [matchId]: { [docId]: { [lineIdx]: string } } }
  const [invAmountSaving, setInvAmountSaving] = useState({}); // { [matchId]: bool }
  const [matchNameChangedFlag, setMatchNameChangedFlag] = useState(false);
  const [acceptingRecon, setAcceptingRecon] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [reopenNotice, setReopenNotice] = useState(null); // { type: 'success' | 'error', text }
  const reopenNoticeRef = useRef(null);
  const reopenBtnRef = useRef(null);
  const focusReopenAfterAccept = useRef(false);
  const [matchDocPreview, setMatchDocPreview] = useState(null);
  const [matchEmailPreview, setMatchEmailPreview] = useState(null);
  const [customDocTypes, setCustomDocTypes] = useState([]);
  const [capturing, setCapturing] = useState(false);
  const [captureResult, setCaptureResult] = useState(null); // { captured, updated } | null
  const [bankConflicts, setBankConflicts] = useState([]); // pending bank conflicts to resolve
  const [acceptedConflicts, setAcceptedConflicts] = useState([]); // supplier names user accepted
  const [conflictDecisions, setConflictDecisions] = useState({}); // { [supplier_name]: 'keep' | 'accept' } — per-row choice, default 'keep'
  // Share token state
  const [shareTokens, setShareTokens] = useState([]);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareForm, setShareForm] = useState({ assigned_to_name: '', assigned_to_email: '' });
  const [shareCreating, setShareCreating] = useState(false);
  const [copiedToken, setCopiedToken] = useState(null);
  // Supplier selection for share link (set of match IDs)
  const [selectedMatchIds, setSelectedMatchIds] = useState(null); // null = all selected

  const loadSession = useCallback(async () => {
    try {
      setError(null);
      const res = await supplierReconSessionsAPI.getSession(id);
      setSession(res.data.session);
      setDocuments(res.data.documents || []);
      setMatches(res.data.matches || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadSession();
    supplierReconAPI.getConfig()
      .then(res => {
        const mappings = res.data.folder_mappings || [];
        setExchangeMappings(mappings.filter(m => m.source === 'exchange'));
      })
      .catch(() => {});
  }, [loadSession]);

  // Load custom doc types from group once session is loaded
  useEffect(() => {
    if (!session?.group_id) return;
    supplierReconAPI.getGroups()
      .then(res => {
        const grp = (res.data.groups || []).find(g => g.id === session.group_id);
        setCustomDocTypes(grp?.custom_doc_types || []);
      })
      .catch(() => {});
  }, [session?.group_id]); // eslint-disable-line

  // Continuously process one doc at a time when in 'processing' status.
  // Each /process call handles exactly one document, then we immediately call again.
  // When remaining === 0, transition to matching loop.
  useEffect(() => {
    if (!session || session.status !== 'processing' || session.recon_accepted) return;
    let cancelled = false;

    const processLoop = async () => {
      while (!cancelled) {
        try {
          const res = await supplierReconSessionsAPI.processSession(session.id);
          if (cancelled) return;
          if (res.data.remaining === 0) {
            // Kick off matching — the matchingLoop effect will take over
            await supplierReconSessionsAPI.runMatching(session.id);
            await loadSession();
            return;
          }
          await loadSession();
        } catch {
          await new Promise(r => setTimeout(r, 5000));
        }
      }
    };

    processLoop();
    return () => { cancelled = true; };
  }, [session?.id, session?.status, session?.recon_accepted]); // eslint-disable-line

  // Continuously match one supplier at a time when in 'matching' status.
  useEffect(() => {
    if (!session || session.status !== 'matching' || session.recon_accepted) return;
    let cancelled = false;

    const matchingLoop = async () => {
      let errorCount = 0;
      let iterCount = 0;
      const MAX_ITERS = 500; // safety cap — no session should ever have 500+ suppliers
      while (!cancelled) {
        iterCount++;
        if (iterCount > MAX_ITERS) {
          console.error('[matchingLoop] Safety cap reached — stopping loop to prevent infinite run');
          await loadSession();
          return;
        }
        try {
          const res = await supplierReconSessionsAPI.runMatching(session.id);
          if (cancelled) return;
          errorCount = 0;
          if (res.data.remaining === 0) {
            await loadSession();
            return;
          }
          await loadSession();
        } catch (err) {
          console.error('[matchingLoop] run-matching error:', err?.response?.data?.error || err?.message);
          errorCount++;
          if (errorCount >= 3) {
            await loadSession();
            return;
          }
          await new Promise(r => setTimeout(r, 5000));
        }
      }
    };

    matchingLoop();
    return () => { cancelled = true; };
  }, [session?.id, session?.status, session?.recon_accepted]); // eslint-disable-line

  // Poll every 30s for syncing status only (processing/matching handled by loops above)
  useEffect(() => {
    if (!session) return;
    if (session.status !== 'syncing') return;
    const timer = setInterval(loadSession, 30000);
    return () => clearInterval(timer);
  }, [session, loadSession]);

  // Poll when any document is individually being re-processed in the background
  // (session is complete/error but a doc has ai_status='processing' from a manual re-process)
  // Use longer interval (15s) when many docs are processing (Re-process All), 6s otherwise
  useEffect(() => {
    if (!session) return;
    if (['processing', 'matching', 'syncing'].includes(session.status)) return; // handled by other loops
    const processingDocs = documents.filter(d => d.ai_status === 'processing' || d.ai_status === 'pending');
    if (processingDocs.length === 0) return;
    const interval = processingDocs.length > 5 ? 15000 : 6000;
    const timer = setInterval(loadSession, interval);
    return () => clearInterval(timer);
  }, [session, documents, loadSession]);

  // Reload report fresh every time the tab is opened
  useEffect(() => {
    if (activeTab !== 'report' || !session || session.status !== 'complete') return;
    setReportData(null);
    setLoadingReport(true);
    supplierReconSessionsAPI.getReport(id)
      .then(res => setReportData(res.data))
      .catch(err => setError(err.response?.data?.error || err.message))
      .finally(() => setLoadingReport(false));
  }, [activeTab, session, id]);

  // Focus handling: move focus to an error notice (success is announced by the live region only,
  // so it is not announced twice); Reopen control after an accept
  useEffect(() => {
    if (reopenNotice && reopenNotice.type === 'error' && reopenNoticeRef.current) reopenNoticeRef.current.focus();
  }, [reopenNotice]);
  // A success notice clears itself after a few seconds
  useEffect(() => {
    if (!reopenNotice || reopenNotice.type !== 'success') return undefined;
    const t = setTimeout(() => setReopenNotice(null), 8000);
    return () => clearTimeout(t);
  }, [reopenNotice]);
  useEffect(() => {
    if (focusReopenAfterAccept.current && session?.recon_accepted && reopenBtnRef.current) {
      reopenBtnRef.current.focus();
      focusReopenAfterAccept.current = false;
    }
  }, [session?.recon_accepted]);

  const handleMarkError = async () => {
    try {
      setMarkingError(true);
      await supplierReconSessionsAPI.markError(id, 'Pipeline timed out — marked as error manually');
      await loadSession();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setMarkingError(false);
    }
  };

  // Server refuses matching on an accepted recon (409 RECON_ACCEPTED): show its message and refresh state.
  const handleAcceptedConflict = (err) => {
    if (err?.response?.status === 409 && err?.response?.data?.code === 'RECON_ACCEPTED') {
      setReopenNotice({ type: 'error', text: err.response.data.error || 'This recon has been accepted. Reopen it before re-running matching.' });
      loadSession();
      return true;
    }
    return false;
  };

  const handleRunMatching = async () => {
    if (!window.confirm('Run AI matching now? This will analyze all processed documents.')) return;
    try {
      setRunningMatching(true);
      // Loop one-supplier-at-a-time until done
      while (true) { // eslint-disable-line no-constant-condition
        const res = await supplierReconSessionsAPI.runMatching(id);
        if (res.data.remaining === 0) break;
      }
      await loadSession();
    } catch (err) {
      if (!handleAcceptedConflict(err)) setError(err.response?.data?.error || err.message);
    } finally {
      setRunningMatching(false);
    }
  };

  const handleRerunMatching = async () => {
    if (session?.recon_accepted) return; // must be reopened first
    if (!window.confirm('Re-run matching? This will delete existing match results and recalculate from current document data.')) return;
    try {
      setRunningMatching(true);
      setMatches([]); // Clear immediately so counters reset while matching runs
      // First call uses reset=true to clear existing matches; subsequent calls continue normally
      let first = true;
      while (true) { // eslint-disable-line no-constant-condition
        const res = first
          ? await supplierReconSessionsAPI.rerunMatching(id)
          : await supplierReconSessionsAPI.runMatching(id);
        first = false;
        if (res.data.remaining === 0) break;
      }
      await loadSession();
    } catch (err) {
      if (!handleAcceptedConflict(err)) setError(err.response?.data?.error || err.message);
    } finally {
      setRunningMatching(false);
    }
  };

  const handleReopenRecon = async () => {
    if (reopening) return;
    if (!window.confirm("Reopen this recon? It goes back to In Progress so you can re-run matching and re-process documents. Your decisions, payment choices, edits and accounting links are kept. Files already moved to the recon folder in SharePoint are not moved back.")) return;
    setReopening(true);
    setReopenNotice(null);
    try {
      await supplierReconSessionsAPI.reopenRecon(id);
      setSession(prev => prev ? { ...prev, recon_accepted: false, recon_accepted_at: null, recon_accepted_by: null } : prev);
      setReportData(prev => prev ? { ...prev, session: { ...prev.session, recon_accepted: false, recon_accepted_at: null, recon_accepted_by: null } } : prev);
      setReopenNotice({ type: 'success', text: 'Recon reopened. It is back In Progress and can be accepted again.' });
    } catch (err) {
      setReopenNotice({ type: 'error', text: 'Failed to reopen recon: ' + (err?.response?.data?.error || err.message) });
    } finally {
      setReopening(false);
    }
  };

  const handleCaptureToRegistry = async (forceConflicts = []) => {
    setCapturing(true);
    setCaptureResult(null);
    setBankConflicts([]);
    try {
      const res = await suppliersAPI.autoCapture(id, forceConflicts);
      if (res.data.bank_conflicts && res.data.bank_conflicts.length > 0) {
        // Pause and show conflict modal — user must decide before completing capture.
        // Default every row to 'keep' (safe — matches prior behaviour when unresolved).
        setBankConflicts(res.data.bank_conflicts);
        setConflictDecisions(Object.fromEntries(res.data.bank_conflicts.map(c => [c.supplier_name, 'keep'])));
        setCaptureResult({ captured: res.data.captured, updated: res.data.updated });
      } else {
        setCaptureResult(res.data);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setCapturing(false);
    }
  };

  // Submits each conflict per its own row decision — a mix of accepted and kept
  // suppliers in the same batch is fully supported by the backend's force_conflicts array.
  const handleResolveBankConflicts = async () => {
    const resolved = bankConflicts
      .filter(c => conflictDecisions[c.supplier_name] === 'accept')
      .map(c => c.supplier_name);
    setBankConflicts([]);
    setConflictDecisions({});
    if (resolved.length > 0) {
      // Re-run capture with only the accepted conflicts forced through; kept ones stay skipped
      await handleCaptureToRegistry([...acceptedConflicts, ...resolved]);
    }
    setAcceptedConflicts([]);
    // Rows left on 'keep' complete with their bank details left unchanged
  };

  // ── Share token handlers ───────────────────────────────────────────────────
  const loadShareTokens = useCallback(async () => {
    if (!id) return;
    try {
      const res = await reconShareAPI.listTokens(id);
      setShareTokens(res.data.tokens || []);
    } catch { /* non-fatal */ }
  }, [id]);

  const handleCreateShareToken = async () => {
    if (!shareForm.assigned_to_name.trim()) return;
    setShareCreating(true);
    try {
      // selectedMatchIds is always a concrete Set at this point (the modal ensures it)
      const payload = {
        ...shareForm,
        included_match_ids: selectedMatchIds ? [...selectedMatchIds] : null,
      };
      const res = await reconShareAPI.createToken(id, payload);
      const newTokens = [res.data.token, ...shareTokens];
      setShareTokens(newTokens);
      setShareForm({ assigned_to_name: '', assigned_to_email: '' });
      // Recompute selectable after this new token is added (it's not accepted yet, so doesn't affect alreadyApproved)
      // Use null so the modal IIFE re-derives from the updated token list on next render
      setSelectedMatchIds(null);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setShareCreating(false);
    }
  };

  const handleRevokeToken = async (tokenId) => {
    await reconShareAPI.revokeToken(id, tokenId);
    setShareTokens(prev => prev.map(t => t.id === tokenId ? { ...t, is_active: false } : t));
  };

  const handleCopyLink = (token) => {
    const url = `${window.location.origin}/r/${token}`;
    navigator.clipboard.writeText(url);
    setCopiedToken(token);
    setTimeout(() => setCopiedToken(null), 2000);
  };

  // Load share tokens when switching to report tab
  useEffect(() => {
    if (activeTab === 'report') loadShareTokens();
  }, [activeTab, loadShareTokens]);

  const patchDoc = async (docId, updates) => {
    setDocuments(prev => prev.map(d => d.id === docId ? { ...d, ...updates } : d));
    try {
      await supplierReconSessionsAPI.updateDocument(docId, updates);
    } catch (err) {
      console.error('Doc patch error:', err);
      await loadSession();
    }
  };

  const patchMatch = async (matchId, updates) => {
    setMatchSaving(prev => ({ ...prev, [matchId]: true }));
    setMatches(prev => prev.map(m => m.id === matchId ? { ...m, ...updates } : m));
    try {
      const res = await supplierReconSessionsAPI.updateMatch(matchId, updates);
      setMatches(prev => prev.map(m => m.id === matchId ? { ...m, ...res.data } : m));
    } catch (err) {
      console.error('Match patch error:', err);
      await loadSession(); // revert
    } finally {
      setMatchSaving(prev => ({ ...prev, [matchId]: false }));
    }
  };

  const handleMatchAccept = (m) => {
    const lockedDiscount = matchDiscount[m.id]?.locked;
    // Store the discounted amount so Report can pre-fill it, but leave payment_status null
    // so the user still has to explicitly confirm the payment decision in Report.
    const updates = { review_status: 'accepted', review_note: m.review_note || null, payment_status: null };
    if (lockedDiscount != null) {
      updates.payment_amount = lockedDiscount;
    }
    setMatchDiscount(prev => { const n = { ...prev }; delete n[m.id]; return n; });
    patchMatch(m.id, updates);
  };

  const handleMatchReject = (m) => {
    if (matchEditingNote[m.id] === undefined) {
      setMatchEditingNote(prev => ({ ...prev, [m.id]: m.review_note || '' }));
    } else {
      patchMatch(m.id, { review_status: 'rejected', review_note: matchEditingNote[m.id] || null });
      setMatchEditingNote(prev => { const n = { ...prev }; delete n[m.id]; return n; });
    }
  };

  const handlePlLineEdit = (matchId, idx, field, value) => {
    setPlLineEdits(prev => ({
      ...prev,
      [matchId]: { ...(prev[matchId] || {}), [idx]: { ...(prev[matchId]?.[idx] || {}), [field]: value } }
    }));
  };

  const handlePlLineSave = async (matchId, allLines) => {
    setPlLineSaving(prev => ({ ...prev, [matchId]: true }));
    try {
      const edits = plLineEdits[matchId] || {};
      const updatedLines = allLines.map((li, idx) => ({
        ...li,
        ref: edits[idx]?.ref !== undefined ? edits[idx].ref : li.ref,
        amount: edits[idx]?.amount !== undefined ? parseFloat(edits[idx].amount) : li.amount,
      }));
      const updated = await supplierReconSessionsAPI.updateMatch(matchId, { pl_lines: updatedLines });
      // Spread full response so recalculated totals (statement_total, variance, match_status etc.) update in the card
      setMatches(prev => prev.map(m => m.id === matchId ? { ...m, ...updated.data } : m));
      setPlLineEdits(prev => { const n = { ...prev }; delete n[matchId]; return n; });
    } catch (err) {
      console.error('Failed to save PL lines:', err);
    } finally {
      setPlLineSaving(prev => ({ ...prev, [matchId]: false }));
    }
  };

  const handleInvAmountSave = async (matchId, invoiceDocs) => {
    setInvAmountSaving(prev => ({ ...prev, [matchId]: true }));
    try {
      const edits = invAmountEdits[matchId] || {};
      const lineEdits = invLineEdits[matchId] || {};
      await Promise.all(
        invoiceDocs.map(doc => {
          const docLineEdits = lineEdits[doc.id];
          if (docLineEdits && Object.keys(docLineEdits).length > 0) {
            // Line-level edits: update ai_raw.line_items amounts and set user_amount to their sum
            // invLines indices (0,1,...) match the keys in docLineEdits
            let invLineIdx = 0;
            const updatedLines = (doc.ai_raw?.line_items || []).map(li => {
              if (li.type !== 'invoice' || li.amount == null) return li;
              const raw = docLineEdits[invLineIdx++];
              if (raw === undefined) return li;
              const parsed = parseFloat(raw);
              return { ...li, amount: isNaN(parsed) ? li.amount : parsed };
            });
            const lineSum = updatedLines
              .filter(li => li.type === 'invoice' && li.amount != null)
              .reduce((s, li) => s + parseFloat(li.amount || 0), 0);
            return patchDoc(doc.id, { ai_raw: { ...doc.ai_raw, line_items: updatedLines }, user_amount: lineSum });
          }
          const raw = edits[doc.id];
          if (raw === undefined) return null;
          const parsed = raw === '' ? null : parseFloat(raw);
          const newAmount = isNaN(parsed) ? null : parsed;
          return patchDoc(doc.id, { user_amount: newAmount });
        }).filter(Boolean)
      );
      // Recalculate the match totals from updated document amounts
      const updated = await supplierReconSessionsAPI.updateMatch(matchId, { recalculate_invoice_total: true });
      setMatches(prev => prev.map(m => m.id === matchId ? { ...m, ...updated.data } : m));
      setInvAmountEdits(prev => { const n = { ...prev }; delete n[matchId]; return n; });
      setInvLineEdits(prev => { const n = { ...prev }; delete n[matchId]; return n; });
    } catch (err) {
      console.error('Failed to save invoice amounts:', err);
    } finally {
      setInvAmountSaving(prev => ({ ...prev, [matchId]: false }));
    }
  };

  const handleMatchNameSave = async (matchId) => {
    const newName = (matchEditingName[matchId] || '').trim();
    setMatchEditingName(prev => { const n = { ...prev }; delete n[matchId]; return n; });
    if (!newName) return;
    const match = matches.find(m => m.id === matchId);
    if (!match || newName === match.supplier_name) return;
    // Optimistically update the match card display immediately
    setMatches(prev => prev.map(m => m.id === matchId ? { ...m, supplier_name: newName } : m));
    try {
      // Patch the match record — backend saves alias (old name → new name) automatically
      await supplierReconSessionsAPI.updateMatch(matchId, { supplier_name: newName });
      // Also patch user_supplier_name on all linked documents
      const linkedDocIds = match.document_ids || [];
      if (linkedDocIds.length > 0) {
        await Promise.all(linkedDocIds.map(docId =>
          supplierReconSessionsAPI.updateDocument(docId, { user_supplier_name: newName })
        ));
        setDocuments(prev => prev.map(d =>
          linkedDocIds.includes(d.id) ? { ...d, user_supplier_name: newName } : d
        ));
      }
      setMatchNameChangedFlag(true);
    } catch (err) {
      console.error('Match name save error:', err);
      setMatches(prev => prev.map(m => m.id === matchId ? { ...m, supplier_name: match.supplier_name } : m));
    }
  };

  const handleMatchDocView = (doc) => {
    if (doc.source === 'exchange') {
      const mailboxId = doc.ai_raw?.mailbox_id || (exchangeMappings && exchangeMappings[0]?.mailbox_id);
      if (mailboxId && doc.external_id) {
        setMatchEmailPreview({ mailboxId, messageId: doc.external_id, subject: doc.file_name });
      } else if (doc.external_url) {
        window.open(doc.external_url, '_blank', 'noopener noreferrer');
      }
    } else {
      setMatchDocPreview(doc);
    }
  };

  const handleMatchDocDownload = async (doc) => {
    try {
      const response = await supplierReconSessionsAPI.downloadDocumentForSave(doc.id);
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.file_name);
      document.body.appendChild(link);
      link.click();
      setTimeout(() => { link.remove(); window.URL.revokeObjectURL(url); }, 100);
    } catch (err) {
      console.error('Download error:', err);
    }
  };

  const handlePaymentDecision = async (matchId, paymentStatus, paymentAmount, paymentNote) => {
    // Optimistically update local state
    setMatches(prev => prev.map(m => m.id === matchId
      ? { ...m, payment_status: paymentStatus, payment_amount: paymentAmount, payment_note: paymentNote }
      : m
    ));
    try {
      await supplierReconSessionsAPI.updateMatch(matchId, { payment_status: paymentStatus, payment_amount: paymentAmount, payment_note: paymentNote });
    } catch (err) {
      console.error('Payment decision error:', err);
      loadSession(); // revert on failure
    }
  };

  // Free-text note per supplier, independent of payment_note (which is a required reason shown
  // only after a reject/custom-amount decision) — always available, and read-only on the
  // external share link for the approvals team.
  const handleSupplierNoteChange = async (matchId, supplierNote) => {
    setMatches(prev => prev.map(m => m.id === matchId ? { ...m, supplier_note: supplierNote } : m));
    try {
      await supplierReconSessionsAPI.updateMatch(matchId, { supplier_note: supplierNote });
    } catch (err) {
      console.error('Supplier note error:', err);
      loadSession(); // revert on failure
    }
  };

  const handleAcceptRecon = async (payableMatches, totalPayable) => {
    setAcceptingRecon(true);
    try {
      const res = await supplierReconSessionsAPI.acceptRecon(id);
      const payments = res.data.payments || [];
      // Generate CSV payment file
      const rows = [
        ['Supplier', 'Bank Name', 'Bank Account', 'Branch Code', 'Amount'],
        ...payments.map(p => [p.supplier_name, p.bank_name, p.bank_account, p.bank_branch, p.amount]),
      ];
      const csv = rows.map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `payment-${session?.name || id}-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      // Mark session as accepted in local state
      setReopenNotice(null);
      focusReopenAfterAccept.current = true;
      setSession(prev => ({ ...prev, recon_accepted: true }));
      setReportData(prev => prev ? { ...prev, session: { ...prev.session, recon_accepted: true } } : prev);
    } catch (err) {
      console.error('Accept recon error:', err);
      alert('Failed to accept recon: ' + (err?.response?.data?.error || err.message));
    } finally {
      setAcceptingRecon(false);
    }
  };

  const handleDownloadTokenPaymentCSV = async (token) => {
    try {
      const res = await reconShareAPI.tokenPaymentCSV(id, token.id);
      const { payments, assigned_to_name } = res.data;
      const rows = [
        ['Supplier', 'Bank Name', 'Bank Account', 'Branch Code', 'Amount'],
        ...payments.map(p => [p.supplier_name, p.bank_name, p.bank_account, p.bank_branch, p.amount]),
      ];
      const csv = rows.map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `payment-${assigned_to_name.replace(/[^a-zA-Z0-9]/g, '-')}-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to download payment file: ' + (err?.response?.data?.error || err.message));
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <FiLoader className="w-8 h-8 animate-spin text-green-600" />
      </div>
    );
  }

  if (!session) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
          Session not found or failed to load.
        </div>
      </div>
    );
  }

  const inProgress = ['syncing', 'processing', 'matching'].includes(session.status);
  const syncDone = ['processing', 'matching', 'complete', 'error'].includes(session.status);
  const processingDone = ['matching', 'complete', 'error'].includes(session.status);
  const matchingDone = ['complete', 'error'].includes(session.status) && (matches.length > 0 || session.status === 'complete');

  const docsProcessed = documents.filter(d => d.ai_status === 'completed').length;
  const docsError = documents.filter(d => d.ai_status === 'error').length;
  const docsTotal = documents.length;

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'documents', label: `Documents (${docsTotal})` },
    { id: 'matches', label: `Matches (${matches.length})` },
    { id: 'report', label: 'Report', disabled: session.status !== 'complete' },
    { id: 'accounting', label: 'Accounting', disabled: session.status !== 'complete', badge: accountingAiRunning },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-5">
      {/* Back + Header */}
      <div>
        <button
          onClick={() => navigate('/supplier-recon')}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-3 transition-colors"
        >
          <FiArrowLeft className="w-4 h-4" />
          All Recon Runs
        </button>

        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <FiPackage className="w-6 h-6 text-green-600" />
              {session.name}
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">
              {formatDate(session.period_start)} – {formatDate(session.period_end)}
              <span className="mx-2 text-gray-300">·</span>
              Created {formatDate(session.created_at)}
            </p>
            {session.recon_accepted && (
              <p className="text-xs text-gray-600 mt-1">This recon is accepted, so matching and re-processing are locked. Reopen it to make changes.</p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <StatusBadge status={session.status} reconAccepted={!!session.recon_accepted} />
            {session.recon_accepted && (
              <button
                type="button"
                ref={reopenBtnRef}
                onClick={handleReopenRecon}
                disabled={reopening}
                className="px-3 py-2 min-h-[44px] sm:min-h-0 sm:py-1.5 text-xs font-medium text-amber-900 bg-amber-50 border border-amber-300 hover:bg-amber-100 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-1"
                title="Mark this recon as not accepted again"
              >
                {reopening ? <FiLoader className="w-3.5 h-3.5 animate-spin" aria-hidden="true" /> : <FiUnlock className="w-3.5 h-3.5" aria-hidden="true" />}
                Reopen recon
              </button>
            )}
            {(session.status === 'complete' || session.status === 'error') && !session.recon_accepted && (
              <button
                onClick={handleRerunMatching}
                disabled={runningMatching}
                className="px-3 py-1.5 text-xs text-amber-700 border border-amber-200 hover:bg-amber-50 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1"
                title="Re-calculate matching from current document data"
              >
                {runningMatching ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : <FiRefreshCw className="w-3.5 h-3.5" />}
                Re-run Matching
              </button>
            )}
            {session.status === 'complete' && (
              <button
                onClick={() => handleCaptureToRegistry()}
                disabled={capturing}
                className="px-3 py-1.5 text-xs text-green-700 border border-green-200 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1"
                title="Save all suppliers from this session to the permanent Supplier registry"
              >
                {capturing ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : <FiUsers className="w-3.5 h-3.5" />}
                Capture to Suppliers
              </button>
            )}
            {captureResult && (
              <span className="text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-2 py-1">
                {captureResult.captured} new · {captureResult.updated} updated
              </span>
            )}
            {inProgress && (
              <button
                onClick={handleMarkError}
                disabled={markingError}
                className="px-3 py-1.5 text-xs text-red-600 border border-red-200 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                title="Mark session as error if it's stuck"
              >
                {markingError ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : 'Mark as Error'}
              </button>
            )}
            <button
              onClick={loadSession}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Refresh"
            >
              <FiRefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Reopen / accepted-recon notice. Success: announced by the live region (no focus move).
          Error: focus moves to it, so it is kept outside the live region to avoid a double announcement. */}
      <div role="status" aria-live="polite">
        {reopenNotice && reopenNotice.type === 'success' && (
          <div className="rounded-lg p-3 text-sm border bg-green-50 border-green-200 text-green-900 flex items-start justify-between gap-3">
            <span>{reopenNotice.text}</span>
            <button
              type="button"
              onClick={() => setReopenNotice(null)}
              aria-label="Dismiss message"
              className="flex-shrink-0 inline-flex items-center justify-center min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 sm:p-1 -m-2 sm:m-0 rounded text-green-900 hover:bg-green-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
            >
              <FiX className="w-4 h-4" aria-hidden="true" />
            </button>
          </div>
        )}
      </div>
      {reopenNotice && reopenNotice.type === 'error' && (
        <div
          ref={reopenNoticeRef}
          tabIndex={-1}
          className="rounded-lg p-3 text-sm border bg-red-50 border-red-200 text-red-800 flex items-start justify-between gap-3 focus:outline-none focus:ring-2 focus:ring-green-500"
        >
          <span>{reopenNotice.text}</span>
          <button
            type="button"
            onClick={() => setReopenNotice(null)}
            aria-label="Dismiss message"
            className="flex-shrink-0 inline-flex items-center justify-center min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 sm:p-1 -m-2 sm:m-0 rounded text-red-800 hover:bg-red-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
          >
            <FiX className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>
      )}

      {/* Error */}
      {(error || session.error_message) && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <FiAlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-red-700">{error || session.error_message}</p>
        </div>
      )}

      {/* No documents synced warning */}
      {syncDone && docsTotal === 0 && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 flex items-start gap-3">
          <FiAlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-amber-800">No documents were found for this recon period</p>
            <p className="text-sm text-amber-700 mt-0.5">
              No files were synced from the mapped folders for the period {formatDate(session.period_start)} – {formatDate(session.period_end)}.
              Check that your source folders contain documents dated within this range.
            </p>
          </div>
        </div>
      )}

      {/* Date range coverage warning — shown after AI processing completes */}
      {processingDone && docsTotal > 0 && (() => {
        const periodStart = session.period_start;
        const periodEnd = session.period_end;
        const docsWithDateInRange = documents.filter(d => {
          if (!d.period_date) return false;
          return d.period_date >= periodStart && d.period_date <= periodEnd;
        });
        if (docsWithDateInRange.length > 0) return null;
        const docsWithAnyDate = documents.filter(d => d.period_date);
        return (
          <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 flex items-start gap-3">
            <FiAlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-amber-800">No documents fall within the selected date range</p>
              <p className="text-sm text-amber-700 mt-0.5">
                The recon period is {formatDate(periodStart)} – {formatDate(periodEnd)}, but none of the {docsTotal} synced document{docsTotal !== 1 ? 's' : ''} {docsWithAnyDate.length > 0 ? 'have a document date within this range' : 'have a recognised document date'}.
                {docsWithAnyDate.length > 0 && ` Document dates range from ${formatDate(docsWithAnyDate.map(d => d.period_date).sort()[0])} to ${formatDate(docsWithAnyDate.map(d => d.period_date).sort().slice(-1)[0])}.`}
              </p>
              <p className="text-xs text-amber-600 mt-1">
                Check your folder mappings or adjust the recon period to match the documents in your source folders.
              </p>
            </div>
          </div>
        );
      })()}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-1">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => !tab.disabled && setActiveTab(tab.id)}
              disabled={tab.disabled}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-green-500 text-green-700'
                  : tab.disabled
                  ? 'border-transparent text-gray-300 cursor-not-allowed'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
              {tab.badge && (
                <>
                  <span className="ml-1.5 inline-block w-2 h-2 rounded-full bg-blue-600 align-middle motion-safe:animate-pulse" aria-hidden="true" />
                  <span className="sr-only"> (AI matching running)</span>
                </>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab: Overview */}
      {activeTab === 'overview' && (
        <div className="space-y-5">
          {/* Progress steps */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <h3 className="text-sm font-medium text-gray-700 mb-4">Processing Progress</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <ProgressStep label="Sync Documents" done={syncDone} active={session.status === 'syncing'} />
              <ProgressStep label="AI Extraction" done={processingDone} active={session.status === 'processing'} />
              <ProgressStep label="Matching" done={matchingDone} active={session.status === 'matching'} />
              <ProgressStep label="Complete" done={session.status === 'complete'} active={false} />
            </div>

            {session.status === 'processing' && (
              <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                <p className="font-medium">AI extraction running — {docsProcessed} of {docsTotal} done{docsError > 0 ? `, ${docsError} failed (retrying)` : ''}</p>
                <p className="text-xs text-blue-700 mt-0.5">
                  Processing documents one at a time. This page updates automatically.
                  You can leave and come back — the recon will continue.
                </p>
              </div>
            )}
            {session.status === 'matching' && (
              <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                <p className="font-medium">Running AI matching...</p>
                <p className="text-xs text-amber-700 mt-0.5">Comparing invoices, statements and payments across suppliers.</p>
              </div>
            )}
            {inProgress && (
              <div className="mt-3 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-green-400 rounded-full animate-pulse" style={{
                  width: session.status === 'syncing' ? '25%' : session.status === 'processing'
                    ? `${Math.max(10, Math.round((docsProcessed / Math.max(docsTotal, 1)) * 75) + 25)}%`
                    : '90%'
                }} />
              </div>
            )}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-gray-900">{docsTotal}</p>
              <p className="text-xs text-gray-500 mt-1 flex items-center justify-center gap-1">
                <FiFileText className="w-3.5 h-3.5" />Documents synced
              </p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-purple-600">{docsProcessed}</p>
              <p className="text-xs text-gray-500 mt-1">AI processed</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-green-600">{matches.length}</p>
              <p className="text-xs text-gray-500 mt-1 flex items-center justify-center gap-1">
                <FiUsers className="w-3.5 h-3.5" />Suppliers
              </p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-amber-600">
                {matches.filter(m => m.match_status === 'discrepancy').length}
              </p>
              <p className="text-xs text-gray-500 mt-1">Discrepancies</p>
            </div>
          </div>

          {/* Sync summary */}
          {session.sync_summary && Object.keys(session.sync_summary).length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Sync Summary</h3>
              <div className="flex gap-6 text-sm">
                {session.sync_summary.synced != null && (
                  <span className="text-green-600 font-medium">{session.sync_summary.synced} synced</span>
                )}
                {session.sync_summary.failed > 0 && (
                  <span className="text-red-600 font-medium">{session.sync_summary.failed} failed</span>
                )}
                {session.sync_summary.skipped > 0 && (
                  <span className="text-gray-500">{session.sync_summary.skipped} already synced</span>
                )}
                {session.sync_summary.total != null && (
                  <span className="text-gray-400">{session.sync_summary.total} total</span>
                )}
              </div>
            </div>
          )}

          {/* Manual matching trigger */}
          {session.status === 'processing' && (docsProcessed + docsError) === docsTotal && docsTotal > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center justify-between gap-4">
              <p className="text-sm text-amber-800">
                {docsProcessed} of {docsTotal} documents processed{docsError > 0 ? ` (${docsError} failed)` : ''}. Ready to run AI matching.
              </p>
              <button
                onClick={handleRunMatching}
                disabled={runningMatching}
                className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white text-sm font-medium rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50"
              >
                {runningMatching
                  ? <FiLoader className="w-4 h-4 animate-spin" />
                  : <FiPlay className="w-4 h-4" />
                }
                Run Matching
              </button>
            </div>
          )}
        </div>
      )}

      {/* Tab: Documents */}
      {activeTab === 'documents' && (
        <DocumentsTab
          documents={documents}
          exchangeMappings={exchangeMappings}
          matches={matches}
          onReprocessed={loadSession}
          onRerunMatching={session.status === 'complete' && !session.recon_accepted ? handleRerunMatching : undefined}
          customDocTypes={customDocTypes}
          sessionId={session.id}
          reconAccepted={!!session.recon_accepted}
          onReopenRecon={handleReopenRecon}
          reopening={reopening}
          onAcceptedConflict={handleAcceptedConflict}
        />
      )}

      {/* Tab: Matches */}
      {activeTab === 'matches' && (
        <div>
          {matchDocPreview && <FilePreviewModal doc={matchDocPreview} onClose={() => setMatchDocPreview(null)} />}
          {matchEmailPreview && (
            <EmailDetailModal
              mailboxId={matchEmailPreview.mailboxId}
              messageId={matchEmailPreview.messageId}
              subject={matchEmailPreview.subject}
              onClose={() => setMatchEmailPreview(null)}
            />
          )}
          {matchNameChangedFlag && session.recon_accepted && (
            <div className="mb-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
              <p className="text-sm text-amber-900">Supplier name updated on linked documents. Reopen the recon to re-run matching.</p>
              <button
                type="button"
                onClick={handleReopenRecon}
                disabled={reopening}
                className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-2 min-h-[44px] sm:min-h-0 sm:py-1.5 text-xs font-medium text-amber-900 bg-amber-100 border border-amber-300 rounded-lg hover:bg-amber-200 transition-colors disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-1"
              >
                <FiUnlock className="w-3.5 h-3.5" aria-hidden="true" />
                Reopen recon
              </button>
            </div>
          )}
          {matchNameChangedFlag && !session.recon_accepted && (
            <div className="mb-3 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 flex items-center justify-between gap-3">
              <p className="text-sm text-blue-800">Supplier name updated on linked documents — re-run matching to merge results.</p>
              <button
                onClick={() => { setMatchNameChangedFlag(false); handleRerunMatching(); }}
                className="flex-shrink-0 px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Re-run Matching
              </button>
            </div>
          )}
          {matches.length === 0 ? (
            <div className="text-center py-12 text-gray-400 text-sm">
              No supplier matches yet.
              {session.status === 'processing' && ' Matching will run after AI extraction completes.'}
            </div>
          ) : (
            <div className="space-y-2">
              {matches.map(m => {
                const isRejected = m.review_status === 'rejected';
                const isAccepted = m.review_status === 'accepted';
                const isSaving = matchSaving[m.id];
                const isEditingNote = matchEditingNote[m.id] !== undefined;
                const statusColor = {
                  matched: 'text-green-600',
                  discrepancy: 'text-amber-600',
                  unmatched: 'text-red-600',
                  pending: 'text-gray-400',
                }[m.match_status] || 'text-gray-400';
                const invVsStmt = m.invoice_vs_statement;
                const ledgerVar = m.ledger_variance;
                // Dynamic column config from stored ordered_types (or fall back to default)
                const mOrderedTypes = m.ordered_types?.length ? m.ordered_types : DEFAULT_ORDERED_TYPES;
                const mPrimaryType = m.primary_type || mOrderedTypes[0] || 'supplier_invoices';
                const mSecondaryType = m.secondary_type || mOrderedTypes.find(t => t !== mPrimaryType && FINANCIAL_ROLES[t] !== 'payments') || null;
                const mLedgerType = mOrderedTypes.find(t => FINANCIAL_ROLES[t] === 'ledger') || null;
                const mHasPayments = mOrderedTypes.some(t => FINANCIAL_ROLES[t] === 'payments');
                const mPrimaryLabel = TYPE_SHORT[mPrimaryType] || mPrimaryType;
                const mSecondaryLabel = mSecondaryType ? (TYPE_SHORT[mSecondaryType] || mSecondaryType) : null;
                return (
                  <div key={m.id} className={`bg-white border rounded-xl p-4 transition-colors ${
                    isRejected ? 'border-red-200 bg-red-50/30 opacity-70' : isAccepted ? 'border-green-200' : 'border-gray-200'
                  }`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      {/* Left: supplier name + review badge + note */}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          {matchEditingName[m.id] !== undefined ? (
                            <span className="flex items-center gap-1">
                              <input
                                autoFocus
                                value={matchEditingName[m.id]}
                                onChange={e => setMatchEditingName(prev => ({ ...prev, [m.id]: e.target.value }))}
                                onKeyDown={e => { if (e.key === 'Enter') handleMatchNameSave(m.id); if (e.key === 'Escape') setMatchEditingName(prev => { const n={...prev}; delete n[m.id]; return n; }); }}
                                className="text-sm font-semibold border border-blue-300 rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-400 bg-blue-50 text-blue-900 w-48"
                              />
                              <button onClick={() => handleMatchNameSave(m.id)} className="text-xs text-green-600 hover:text-green-800 font-medium px-1" title="Save">✓</button>
                              <button onClick={() => setMatchEditingName(prev => { const n={...prev}; delete n[m.id]; return n; })} className="text-xs text-gray-400 hover:text-gray-600 px-1" title="Cancel">✕</button>
                            </span>
                          ) : (
                            <button
                              onClick={() => setMatchEditingName(prev => ({ ...prev, [m.id]: m.supplier_name || '' }))}
                              title="Click to rename supplier"
                              className={`text-sm font-semibold text-left hover:text-blue-700 transition-colors ${isRejected ? 'text-gray-400 line-through' : 'text-gray-900'}`}
                            >
                              {m.supplier_name}
                            </button>
                          )}
                          <span className={`text-xs font-medium capitalize ${statusColor}`}>{m.match_status}</span>
                          {isAccepted && (
                            <span className="text-xs text-green-600 font-medium">
                              ✓ Accepted{m.payment_amount != null ? ` (${fmt(m.payment_amount)})` : ''}
                            </span>
                          )}
                          {isAccepted && m.payment_amount != null && m.variance != null && (
                            <span className="text-xs text-orange-600 font-medium">
                              {`-${parseFloat(((1 - m.payment_amount / m.variance) * 100).toFixed(2))}% (${fmt(m.variance - m.payment_amount)} discount)`}
                            </span>
                          )}
                          {isRejected && <span className="text-xs text-red-500 font-medium">✗ Rejected</span>}
                        </div>
                        {isRejected && m.review_note && (
                          <p className="mt-1 text-xs text-red-500 italic">Note: {m.review_note}</p>
                        )}
                      </div>

                      {/* Right: Accept/Reject/Discount buttons */}
                      <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
                        {/* Discount inline editor */}
                        {matchDiscount[m.id] && !matchDiscount[m.id].locked && !isAccepted ? (() => {
                          const disc = matchDiscount[m.id];
                          const base = parseFloat(m.variance) || 0;
                          const val = parseFloat(disc.input);
                          let preview = null;
                          if (!isNaN(val) && val >= 0) {
                            preview = disc.mode === 'pct'
                              ? Math.round(base * (1 - Math.min(val, 100) / 100) * 100) / 100
                              : Math.max(0, Math.round((base - val) * 100) / 100);
                          }
                          const lockDiscount = () => {
                            if (preview == null) return;
                            setMatchDiscount(prev => ({ ...prev, [m.id]: { ...prev[m.id], locked: preview } }));
                          };
                          return (
                            <div className="flex items-center gap-1 flex-wrap">
                              <div className="flex rounded overflow-hidden border border-orange-300 text-xs">
                                <button onClick={() => setMatchDiscount(prev => ({ ...prev, [m.id]: { ...prev[m.id], mode: 'pct' } }))}
                                  className={`px-2 py-1 ${disc.mode === 'pct' ? 'bg-orange-500 text-white' : 'bg-white text-orange-600 hover:bg-orange-50'}`}>%</button>
                                <button onClick={() => setMatchDiscount(prev => ({ ...prev, [m.id]: { ...prev[m.id], mode: 'value' } }))}
                                  className={`px-2 py-1 ${disc.mode === 'value' ? 'bg-orange-500 text-white' : 'bg-white text-orange-600 hover:bg-orange-50'}`}>R</button>
                              </div>
                              <input
                                type="number"
                                className="w-20 text-xs border border-orange-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-orange-400"
                                value={disc.input}
                                onChange={e => setMatchDiscount(prev => ({ ...prev, [m.id]: { ...prev[m.id], input: e.target.value } }))}
                                placeholder={disc.mode === 'pct' ? '% off' : 'R off'}
                                autoFocus
                                onKeyDown={e => {
                                  if (e.key === 'Escape') setMatchDiscount(prev => { const n = { ...prev }; delete n[m.id]; return n; });
                                  if (e.key === 'Enter') lockDiscount();
                                }}
                              />
                              {preview != null && (
                                <span className="text-xs text-orange-700 font-medium whitespace-nowrap">= {fmt(preview)}</span>
                              )}
                              <button
                                onClick={lockDiscount}
                                disabled={preview == null}
                                className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-40"
                              >
                                <FiCheck className="w-3 h-3" /> Set
                              </button>
                              <button onClick={() => setMatchDiscount(prev => { const n = { ...prev }; delete n[m.id]; return n; })}
                                className="text-xs text-gray-400 hover:text-gray-600 px-1">✕</button>
                            </div>
                          );
                        })() : (
                          <>
                            {!isAccepted && (
                              <>
                                {matchDiscount[m.id]?.locked != null ? (
                                  <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-orange-100 text-orange-700 border border-orange-300 rounded-lg">
                                    {fmt(matchDiscount[m.id].locked)}
                                    <button
                                      onClick={() => setMatchDiscount(prev => ({ ...prev, [m.id]: { mode: 'pct', input: '' } }))}
                                      className="ml-1 text-orange-400 hover:text-orange-600"
                                      title="Edit discount"
                                    >✕</button>
                                  </span>
                                ) : (
                                  <button
                                    onClick={() => setMatchDiscount(prev => ({ ...prev, [m.id]: { mode: 'pct', input: '' } }))}
                                    disabled={isSaving}
                                    className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-lg border border-orange-300 text-orange-600 bg-white hover:bg-orange-50 transition-colors disabled:opacity-40"
                                    title="Accept with a discount"
                                  >
                                    Discount
                                  </button>
                                )}
                              </>
                            )}
                            <button
                              onClick={() => handleMatchAccept(m)}
                              disabled={isSaving || isAccepted}
                              className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-lg border transition-colors disabled:opacity-40 ${
                                isAccepted ? 'bg-green-100 text-green-700 border-green-300' : 'bg-white text-green-600 border-green-200 hover:bg-green-50'
                              }`}
                              title="Accept this supplier match"
                            >
                              <FiThumbsUp className="w-3.5 h-3.5" />
                              {isAccepted ? 'Accepted' : 'Accept'}
                            </button>
                            <button
                              onClick={() => handleMatchReject(m)}
                              disabled={isSaving}
                              className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-lg border transition-colors disabled:opacity-40 ${
                                isRejected ? 'bg-red-100 text-red-600 border-red-300' : 'bg-white text-red-500 border-red-200 hover:bg-red-50'
                              }`}
                              title={isEditingNote ? 'Confirm rejection' : 'Reject this supplier match'}
                            >
                              <FiThumbsDown className="w-3.5 h-3.5" />
                              {isEditingNote ? 'Confirm' : 'Reject'}
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Rejection note input */}
                    {isEditingNote && (
                      <div className="mt-2 flex items-start gap-2">
                        <textarea
                          value={matchEditingNote[m.id]}
                          onChange={e => setMatchEditingNote(prev => ({ ...prev, [m.id]: e.target.value }))}
                          placeholder="Reason for rejection (optional)..."
                          rows={2}
                          className="flex-1 text-xs border border-red-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-red-400 resize-none"
                        />
                        <button
                          onClick={() => setMatchEditingNote(prev => { const n = {...prev}; delete n[m.id]; return n; })}
                          className="text-xs text-gray-400 hover:text-gray-600 mt-1"
                        >Cancel</button>
                      </div>
                    )}

                    {/* Financial data grid — dynamic columns based on ordered_types */}
                    {(() => {
                      const linkedDocs2 = m.document_ids?.length > 0 ? documents.filter(d => m.document_ids.includes(d.id)) : [];
                      const activePrimaryDocs = linkedDocs2.filter(d => {
                        const t = d.user_doc_type || d.detected_doc_type || d.document_type;
                        return t === mPrimaryType && d.review_status !== 'rejected';
                      });
                      const liveInvTotal = activePrimaryDocs.length > 0
                        ? activePrimaryDocs.reduce((s, d) => {
                            const invLines = (d.ai_raw?.line_items || []).filter(li => li.type === 'invoice' && li.amount != null);
                            if (invLines.length > 0) {
                              const seen = new Set();
                              return s + invLines.reduce((ls, li) => {
                                const ref = (li.ref || '').trim().toLowerCase();
                                if (ref && seen.has(ref)) return ls;
                                if (ref) seen.add(ref);
                                return ls + (parseFloat(li.amount) || 0);
                              }, 0);
                            }
                            return s + (d.user_amount != null ? d.user_amount : (d.amount || 0));
                          }, 0)
                        : m.invoice_total;
                      const cols = [];
                      // Primary amount column
                      cols.push({ header: mPrimaryLabel, value: <span className="font-medium text-gray-800 whitespace-nowrap">{fmt(liveInvTotal)}</span> });
                      // Secondary amount column (if present)
                      if (mSecondaryType) {
                        cols.push({ header: mSecondaryLabel, value: (
                          <span className="font-medium text-gray-800 whitespace-nowrap">
                            {m.statement_count > 0 ? fmt(m.statement_total) : <span className="text-gray-300">—</span>}
                          </span>
                        )});
                        // Primary vs Secondary comparison
                        cols.push({ header: `${mPrimaryLabel} vs ${mSecondaryLabel}`, value: (
                          <span className={`font-medium whitespace-nowrap ${invVsStmt == null ? 'text-gray-300' : Math.abs(invVsStmt) < 0.01 ? 'text-green-600' : 'text-amber-600'}`}>
                            {invVsStmt == null ? '—' : Math.abs(invVsStmt) < 0.01 ? '✓' : fmt(invVsStmt)}
                          </span>
                        )});
                      }
                      // Ledger column + variance (if ledger type included)
                      if (mLedgerType) {
                        cols.push({ header: 'Ledger', value: (
                          <span className="font-medium text-gray-800 whitespace-nowrap">
                            {m.ledger_count > 0 ? fmt(Math.abs(m.ledger_total)) : <span className="text-gray-300">—</span>}
                          </span>
                        )});
                        cols.push({ header: 'Ledger Var', value: (
                          <span className={`font-medium whitespace-nowrap ${m.ledger_count === 0 ? 'text-gray-300' : Math.abs(ledgerVar ?? 0) < 0.01 ? 'text-green-600' : 'text-amber-600'}`}>
                            {m.ledger_count === 0 ? '—' : Math.abs(ledgerVar ?? 0) < 0.01 ? '✓' : fmt(ledgerVar)}
                          </span>
                        )});
                      }
                      // Paid column (if any payment type included)
                      if (mHasPayments) {
                        cols.push({ header: 'Paid', value: <span className="font-medium text-gray-800 whitespace-nowrap">{fmt(m.payments_total)}</span> });
                      }
                      // Outstanding (always shown)
                      cols.push({ header: 'Outstanding', value: (
                        <span className={`font-medium whitespace-nowrap ${(m.variance ?? 0) > 0.01 ? 'text-amber-700' : (m.variance ?? 0) < -0.01 ? 'text-red-600' : 'text-green-600'}`}>
                          {fmt(m.variance)}
                        </span>
                      )});
                      // Confidence — only shown when both primary AND secondary docs are actually present
                      const hasBothSides = (mSecondaryType && m.invoice_total > 0 && m.statement_total > 0) ||
                                           (mLedgerType && m.ledger_count > 0 && (m.invoice_total > 0 || m.statement_total > 0));
                      if (hasBothSides) {
                        cols.push({ header: 'Conf.', value: <span className="text-gray-500">{m.confidence_score != null ? `${Math.round(m.confidence_score * 100)}%` : '—'}</span> });
                      }

                      const colCount = cols.length;
                      return (
                        <div className={`mt-3 grid gap-x-4 gap-y-1 text-xs border-t border-gray-100 pt-3`} style={{ gridTemplateColumns: `repeat(${colCount}, minmax(0, 1fr))` }}>
                          {cols.map(c => <div key={c.header} className="text-gray-500 whitespace-nowrap">{c.header}</div>)}
                          {cols.map(c => <div key={`v-${c.header}`}>{c.value}</div>)}
                        </div>
                      );
                    })()}

                    {/* Linked Documents + Missing Invoices */}
                    {(() => {
                      const linkedDocs = m.document_ids?.length > 0 ? documents.filter(d => m.document_ids.includes(d.id)) : [];

                      // Build cross-ref sets from statements and ledgers in this match
                      const effectiveDocType = (d) => d.user_doc_type || d.detected_doc_type || d.document_type;
                      const stmtDocs = linkedDocs.filter(d => effectiveDocType(d) === 'supplier_statements');
                      const ledgerDocs = linkedDocs.filter(d => effectiveDocType(d) === 'company_supplier_ledgers');
                      const stmtRefList = stmtDocs.flatMap(d => (d.ai_raw?.invoice_refs || []).map(r => r.toLowerCase().trim()));
                      const ledgerRefList = ledgerDocs.flatMap(d => (d.ai_raw?.invoice_refs || []).map(r => r.toLowerCase().trim()));
                      const stmtRefs = new Set(stmtRefList);
                      const ledgerRefs = new Set(ledgerRefList);
                      // Fuzzy ref match: exact OR substring (handles SAP full doc numbers like 5105029292 vs 029292)
                      const refMatchesList = (invRef, refList) => refList.some(r => r === invRef || r.includes(invRef) || invRef.includes(r));
                      const hasStmt = stmtDocs.length > 0;
                      const hasLedger = ledgerDocs.length > 0;

                      // Refs in statement/ledger that have no matching invoice doc in this session
                      const syncedInvRefs = linkedDocs
                        .filter(d => effectiveDocType(d) === 'supplier_invoices' && d.supplier_ref)
                        .map(d => d.supplier_ref.toLowerCase().trim());
                      // Missing = ledger/stmt ref has no synced invoice that fuzzy-matches it
                      const allReferencedRefs = new Set([...stmtRefList, ...ledgerRefList]);
                      const missingRefs = [...allReferencedRefs].filter(r => !syncedInvRefs.some(inv => inv === r || r.includes(inv) || inv.includes(r))).sort();

                      if (linkedDocs.length === 0 && missingRefs.length === 0) return null;
                      return (
                        <div className="mt-3 border-t border-gray-100 pt-3 space-y-3">
                          {linkedDocs.length > 0 && (
                            <div>
                              <p className="text-xs font-medium text-gray-500 mb-2">Linked Documents ({linkedDocs.length})</p>
                              <div className="space-y-1">
                                {linkedDocs.map(doc => {
                                  const docEffType = effectiveDocType(doc);
                                  const effectiveAmount = doc.user_amount != null ? doc.user_amount : doc.amount;
                                  const isDocRejected = doc.review_status === 'rejected';
                                  const isInvoice = docEffType === 'supplier_invoices';
                                  const ref = doc.supplier_ref?.toLowerCase().trim();
                                  return (
                                    <div key={doc.id}>
                                      <div className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs ${isDocRejected ? 'opacity-50 bg-red-50' : 'bg-gray-50'}`}>
                                        {doc.source === 'sharepoint'
                                          ? <FiCloud className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />
                                          : doc.source === 'doxolve'
                                            ? <FiDatabase className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                                            : <FiMail className="w-3.5 h-3.5 text-purple-400 flex-shrink-0" />
                                        }
                                        <span className="text-gray-400 flex-shrink-0">{DOC_TYPE_LABELS[docEffType] || customDocTypes.find(t => t.name === docEffType)?.name || docEffType}</span>
                                        <span className={`flex-1 truncate font-medium ${isDocRejected ? 'text-gray-400 line-through' : 'text-gray-700'}`} title={doc.file_name}>
                                          {doc.file_name}
                                        </span>
                                        {doc.supplier_ref && <span className="text-gray-400 flex-shrink-0 max-w-[200px] truncate" title={doc.supplier_ref}>#{doc.supplier_ref}</span>}
                                        {/* Cross-ref badges for invoices */}
                                        {isInvoice && ref && hasStmt && (() => {
                                          const onStmt = doc.user_on_statement !== null && doc.user_on_statement !== undefined ? doc.user_on_statement : refMatchesList(ref, stmtRefList);
                                          const userOverride = doc.user_on_statement !== null && doc.user_on_statement !== undefined;
                                          return (
                                            <span className={`flex-shrink-0 text-xs px-1 py-0.5 rounded font-medium ${onStmt ? 'bg-green-100 text-green-700' : 'bg-red-50 text-red-500'}`}
                                              title={onStmt ? `Found in statement${userOverride ? ' (manually confirmed)' : ''}` : 'Not in statement'}>
                                              Stmt {onStmt ? '✓' : '✗'}
                                            </span>
                                          );
                                        })()}
                                        {isInvoice && ref && hasLedger && (() => {
                                          const onLedger = doc.user_on_ledger !== null && doc.user_on_ledger !== undefined ? doc.user_on_ledger : refMatchesList(ref, ledgerRefList);
                                          const userOverride = doc.user_on_ledger !== null && doc.user_on_ledger !== undefined;
                                          return (
                                            <span className={`flex-shrink-0 text-xs px-1 py-0.5 rounded font-medium ${onLedger ? 'bg-green-100 text-green-700' : 'bg-red-50 text-red-500'}`}
                                              title={onLedger ? `Found in ledger${userOverride ? ' (manually confirmed)' : ''}` : 'Not in ledger'}>
                                              Ledger {onLedger ? '✓' : '✗'}
                                            </span>
                                          );
                                        })()}
                                        {isInvoice ? (() => {
                                          const invLines = (doc.ai_raw?.line_items || []).filter(li => li.type === 'invoice' && li.amount != null);
                                          const hasMultiLines = invLines.length > 1;
                                          const docLineEdits = invLineEdits[m.id]?.[doc.id] || {};
                                          const hasLineEdits = Object.keys(docLineEdits).length > 0;
                                          // When multi-line, show live sum from line edits (read-only)
                                          if (hasMultiLines) {
                                            const liveSum = invLines.reduce((s, li, i) => {
                                              const raw = docLineEdits[i];
                                              const amt = raw !== undefined ? parseFloat(raw) : parseFloat(li.amount || 0);
                                              return s + (isNaN(amt) ? 0 : amt);
                                            }, 0);
                                            return (
                                              <span className="flex-shrink-0 w-28 text-xs text-right font-medium text-gray-700">
                                                {fmt(liveSum, doc.currency || 'ZAR')}
                                              </span>
                                            );
                                          }
                                          return (
                                            <input
                                              type="number"
                                              step="0.01"
                                              className={`flex-shrink-0 w-28 text-xs bg-transparent border-b text-right font-medium focus:outline-none ${(invAmountEdits[m.id]?.[doc.id] !== undefined) ? 'border-blue-400 text-gray-700' : 'border-transparent hover:border-blue-200 text-gray-700'}`}
                                              value={invAmountEdits[m.id]?.[doc.id] !== undefined ? invAmountEdits[m.id][doc.id] : (effectiveAmount ?? '')}
                                              onChange={e => setInvAmountEdits(prev => ({ ...prev, [m.id]: { ...(prev[m.id] || {}), [doc.id]: e.target.value } }))}
                                              placeholder="—"
                                            />
                                          );
                                        })() : (
                                          <span className={`flex-shrink-0 font-medium ${effectiveAmount != null ? 'text-gray-700' : 'text-gray-400'}`}>
                                            {effectiveAmount != null ? fmt(effectiveAmount, doc.currency || 'ZAR') : '—'}
                                          </span>
                                        )}
                                        {doc.period_date && <span className="text-gray-400 flex-shrink-0">{formatDate(doc.period_date)}</span>}
                                        {doc.review_status === 'accepted' && <span className="text-green-600 flex-shrink-0">✓</span>}
                                        {doc.review_status === 'rejected' && <span className="text-red-500 flex-shrink-0">✗</span>}
                                        <button onClick={() => handleMatchDocView(doc)} className="p-1 text-gray-400 hover:text-blue-600 flex-shrink-0" title="View">
                                          <FiEye className="w-3.5 h-3.5" />
                                        </button>
                                        <button onClick={() => handleMatchDocDownload(doc)} className="p-1 text-gray-400 hover:text-green-600 flex-shrink-0" title="Download">
                                          <FiDownload className="w-3.5 h-3.5" />
                                        </button>
                                        {isInvoice && (
                                          <button
                                            onClick={() => patchDoc(doc.id, { review_status: isDocRejected ? null : 'rejected' })}
                                            className={`p-1 flex-shrink-0 ${isDocRejected ? 'text-green-500 hover:text-green-700' : 'text-red-400 hover:text-red-600'}`}
                                            title={isDocRejected ? 'Restore document' : 'Exclude duplicate document'}
                                          >
                                            {isDocRejected ? <FiCheckCircle className="w-3.5 h-3.5" /> : <FiTrash2 className="w-3.5 h-3.5" />}
                                          </button>
                                        )}
                                      </div>
                                      {/* Invoice line items breakdown — editable when a single PDF contains multiple invoices */}
                                      {docEffType === 'supplier_invoices' && (() => {
                                        const invLines = (doc.ai_raw?.line_items || []).filter(li => li.type === 'invoice' && li.amount != null);
                                        if (invLines.length <= 1) return null;
                                        const docLineEdits = invLineEdits[m.id]?.[doc.id] || {};
                                        return (
                                          <div className="ml-6 mt-1">
                                            <div className="flex items-center gap-2 px-2 py-0.5 text-xs text-gray-400 font-medium border-b border-orange-100 mb-0.5">
                                              <span className="w-28 flex-shrink-0">Invoice #</span>
                                              <span className="w-24 flex-shrink-0">Date</span>
                                              <span className="flex-1 text-right">Amount</span>
                                            </div>
                                            <div className="space-y-0.5">
                                              {invLines.map((li, i) => {
                                                const lineVal = docLineEdits[i] !== undefined ? docLineEdits[i] : (li.amount ?? '');
                                                const isLineEdited = docLineEdits[i] !== undefined;
                                                return (
                                                  <div key={i} className="flex items-center gap-2 px-2 py-1 rounded text-xs bg-orange-50 text-gray-600">
                                                    <span className="w-28 flex-shrink-0 text-orange-700 font-medium truncate">{li.ref || '—'}</span>
                                                    <span className="w-24 flex-shrink-0 text-gray-400">{li.date || '—'}</span>
                                                    <input
                                                      type="number"
                                                      step="0.01"
                                                      className={`flex-1 text-right bg-transparent border-b font-medium text-gray-700 focus:outline-none ${isLineEdited ? 'border-orange-400' : 'border-transparent hover:border-orange-200'}`}
                                                      value={lineVal}
                                                      onChange={e => setInvLineEdits(prev => ({
                                                        ...prev,
                                                        [m.id]: { ...(prev[m.id] || {}), [doc.id]: { ...(prev[m.id]?.[doc.id] || {}), [i]: e.target.value } }
                                                      }))}
                                                      placeholder="—"
                                                    />
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          </div>
                                        );
                                      })()}
                                      {/* PL line items breakdown — shown under purchase_listing docs */}
                                      {docEffType === 'purchase_listing' && (() => {
                                        const allMatchLines = m.pl_lines || [];
                                        // Build indexed lines first so we always have stable global indices
                                        const plLines = allMatchLines
                                          .map((li, idx) => ({ li, idx }))
                                          .filter(({ li }) => !li._plDocId || li._plDocId === doc.id);
                                        if (plLines.length === 0) return null;
                                        const edits = plLineEdits[m.id] || {};
                                        const hasEdits = Object.keys(edits).length > 0;
                                        return (
                                          <div className="ml-6 mt-1">
                                            <div className="flex items-center gap-2 px-2 py-0.5 text-xs text-gray-400 font-medium border-b border-blue-100 mb-0.5">
                                              <span className="w-24 flex-shrink-0">Ref</span>
                                              <span className="flex-1">Category</span>
                                              <span className="w-20 text-right flex-shrink-0">Amount</span>
                                            </div>
                                            <div className="space-y-0.5">
                                              {plLines.map(({ li, idx }) => {
                                                const edit = edits[idx] || {};
                                                const isEditing = edit.ref !== undefined || edit.amount !== undefined;
                                                return (
                                                  <div key={idx} className="flex items-center gap-2 px-2 py-1 rounded text-xs bg-blue-50 text-gray-600">
                                                    <input
                                                      className={`w-24 flex-shrink-0 bg-transparent border-b text-blue-600 font-medium focus:outline-none ${isEditing ? 'border-blue-400' : 'border-transparent hover:border-blue-200'}`}
                                                      value={edit.ref !== undefined ? edit.ref : (li.ref || '')}
                                                      onChange={e => handlePlLineEdit(m.id, idx, 'ref', e.target.value)}
                                                      placeholder="—"
                                                    />
                                                    <span className="flex-1 text-gray-500 truncate">
                                                      {li.purchase_ref && <span className="text-gray-400 mr-1">{li.purchase_ref}</span>}
                                                      {li.cat || li.supplier_name || '—'}
                                                    </span>
                                                    <input
                                                      className={`w-20 flex-shrink-0 text-right bg-transparent border-b font-medium text-gray-700 focus:outline-none ${isEditing ? 'border-blue-400' : 'border-transparent hover:border-blue-200'}`}
                                                      value={edit.amount !== undefined ? edit.amount : (li.amount ?? '')}
                                                      onChange={e => handlePlLineEdit(m.id, idx, 'amount', e.target.value)}
                                                      placeholder="—"
                                                    />
                                                  </div>
                                                );
                                              })}
                                            </div>
                                            {(() => {
                                              const lineTotal = plLines.reduce((s, { li, idx }) => {
                                                const edit = edits[idx] || {};
                                                const amt = edit.amount !== undefined ? parseFloat(edit.amount) : (li.amount ?? 0);
                                                return s + (parseFloat(amt) || 0);
                                              }, 0);
                                              return (
                                                <div className="flex items-center gap-2 px-2 py-1 border-t border-blue-100 mt-0.5">
                                                  <span className="w-24 flex-shrink-0" />
                                                  <span className="flex-1 text-xs text-gray-400 font-medium">Total</span>
                                                  <span className="w-20 flex-shrink-0 text-right text-xs font-semibold text-gray-700">{fmt(lineTotal, 'ZAR')}</span>
                                                </div>
                                              );
                                            })()}
                                            <div className="flex justify-end gap-2 mt-1">
                                              <button onClick={() => handlePlLineSave(m.id, allMatchLines)} disabled={plLineSaving[m.id]}
                                                className="text-xs px-2 py-0.5 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
                                                {plLineSaving[m.id] ? 'Saving…' : 'Save changes'}
                                              </button>
                                              {hasEdits && (
                                                <button onClick={() => setPlLineEdits(prev => { const n = { ...prev }; delete n[m.id]; return n; })}
                                                  className="text-xs text-gray-400 hover:text-gray-600">Cancel</button>
                                              )}
                                            </div>
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  );
                                })}
                              </div>
                              {/* Invoice amount total + Save changes — mirrors PL line editing */}
                              {(() => {
                                const invoiceDocs = linkedDocs.filter(d => effectiveDocType(d) === 'supplier_invoices' && d.review_status !== 'rejected');
                                if (invoiceDocs.length === 0) return null;
                                const edits = invAmountEdits[m.id] || {};
                                const lineEdits = invLineEdits[m.id] || {};
                                const hasEdits = Object.keys(edits).length > 0 || Object.keys(lineEdits).length > 0;
                                const invTotal = invoiceDocs.reduce((s, doc) => {
                                  const docLineEdits = lineEdits[doc.id] || {};
                                  const invLines = (doc.ai_raw?.line_items || []).filter(li => li.type === 'invoice' && li.amount != null);
                                  if (invLines.length > 1) {
                                    // Sum from line edits
                                    return s + invLines.reduce((ls, li, i) => {
                                      const raw = docLineEdits[i];
                                      const amt = raw !== undefined ? parseFloat(raw) : parseFloat(li.amount || 0);
                                      return ls + (isNaN(amt) ? 0 : amt);
                                    }, 0);
                                  }
                                  const raw = edits[doc.id];
                                  const amt = raw !== undefined ? parseFloat(raw) : (doc.user_amount != null ? doc.user_amount : doc.amount);
                                  return s + (parseFloat(amt) || 0);
                                }, 0);
                                return (
                                  <div className="mt-1">
                                    <div className="flex items-center gap-2 px-2 py-1 border-t border-gray-100">
                                      <span className="flex-1 text-xs text-gray-400 font-medium">Invoice Total</span>
                                      <span className="w-28 flex-shrink-0 text-right text-xs font-semibold text-gray-700">{fmt(invTotal, 'ZAR')}</span>
                                    </div>
                                    <div className="flex justify-end gap-2 mt-1">
                                      <button
                                        onClick={() => handleInvAmountSave(m.id, invoiceDocs)}
                                        disabled={invAmountSaving[m.id]}
                                        className="text-xs px-2 py-0.5 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                                      >
                                        {invAmountSaving[m.id] ? 'Saving…' : 'Save changes'}
                                      </button>
                                      {hasEdits && (
                                        <button
                                          onClick={() => {
                                            setInvAmountEdits(prev => { const n = { ...prev }; delete n[m.id]; return n; });
                                            setInvLineEdits(prev => { const n = { ...prev }; delete n[m.id]; return n; });
                                          }}
                                          className="text-xs text-gray-400 hover:text-gray-600"
                                        >
                                          Cancel
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                          )}

                          {/* Missing invoices — split by source (statement vs ledger) */}
                          {(() => {
                            // Build amount lookup from line_items of stmt and ledger docs
                            const stmtLineItems = stmtDocs.flatMap(d => d.ai_raw?.line_items || []);
                            const ledgerLineItems = ledgerDocs.flatMap(d => d.ai_raw?.line_items || []);
                            const stmtAmountMap = {};
                            const stmtDateMap = {};
                            for (const li of stmtLineItems) {
                              if (li.ref) {
                                const k = li.ref.toLowerCase().trim();
                                stmtAmountMap[k] = li.amount ?? null;
                                if (li.date) stmtDateMap[k] = li.date;
                              }
                            }
                            const ledgerAmountMap = {};
                            const ledgerDateMap = {};
                            for (const li of ledgerLineItems) {
                              if (li.ref) {
                                const k = li.ref.toLowerCase().trim();
                                ledgerAmountMap[k] = li.amount ?? null;
                                if (li.date) ledgerDateMap[k] = li.date;
                              }
                            }

                            const invRefMatched = (r) => syncedInvRefs.some(inv => inv === r || r.includes(inv) || inv.includes(r));
                            const missingStmtOnly = [...stmtRefs].filter(r => !invRefMatched(r) && !ledgerRefs.has(r)).sort();
                            const missingLedgerOnly = [...ledgerRefs].filter(r => !invRefMatched(r) && !stmtRefs.has(r)).sort();
                            const missingBoth = [...stmtRefs].filter(r => !invRefMatched(r) && ledgerRefs.has(r)).sort();

                            const totalMissing = missingStmtOnly.length + missingLedgerOnly.length + missingBoth.length;
                            if (totalMissing === 0) return null;

                            const MissingRow = ({ invoiceRef, stmtAmt, ledgerAmt, stmtDate, ledgerDate }) => {
                              const date = stmtDate || ledgerDate;
                              return (
                                <div className="flex items-center gap-3 px-2 py-1.5 bg-amber-50 border border-amber-100 rounded text-xs">
                                  <FiFileText className="w-3 h-3 text-amber-500 flex-shrink-0" />
                                  <span className="font-medium text-amber-900 w-20 flex-shrink-0">{invoiceRef}</span>
                                  {date && <span className="text-gray-400 flex-shrink-0">{formatDate(date)}</span>}
                                  {stmtAmt != null && <span className="text-gray-600">Stmt: <span className="font-medium">{fmt(stmtAmt)}</span></span>}
                                  {ledgerAmt != null && <span className="text-gray-600">Ledger: <span className="font-medium">{fmt(ledgerAmt)}</span></span>}
                                </div>
                              );
                            };

                            return (
                              <div className="space-y-3">
                                {missingBoth.length > 0 && (
                                  <div>
                                    <p className="text-xs font-medium text-amber-700 mb-1.5 flex items-center gap-1">
                                      <FiAlertTriangle className="w-3.5 h-3.5" />
                                      Missing Invoices — in Statement &amp; Ledger ({missingBoth.length})
                                    </p>
                                    <div className="space-y-1">
                                      {missingBoth.map(r => (
                                        <MissingRow key={r} invoiceRef={r} stmtAmt={stmtAmountMap[r] ?? null} ledgerAmt={ledgerAmountMap[r] ?? null} stmtDate={stmtDateMap[r]} ledgerDate={ledgerDateMap[r]} />
                                      ))}
                                    </div>
                                  </div>
                                )}
                                {missingStmtOnly.length > 0 && (
                                  <div>
                                    <p className="text-xs font-medium text-amber-700 mb-1.5 flex items-center gap-1">
                                      <FiAlertTriangle className="w-3.5 h-3.5" />
                                      Missing Invoices — Statement only ({missingStmtOnly.length})
                                    </p>
                                    <div className="space-y-1">
                                      {missingStmtOnly.map(r => (
                                        <MissingRow key={r} invoiceRef={r} stmtAmt={stmtAmountMap[r] ?? null} ledgerAmt={null} stmtDate={stmtDateMap[r]} />
                                      ))}
                                    </div>
                                  </div>
                                )}
                                {missingLedgerOnly.length > 0 && (
                                  <div>
                                    <p className="text-xs font-medium text-amber-700 mb-1.5 flex items-center gap-1">
                                      <FiAlertTriangle className="w-3.5 h-3.5" />
                                      Missing Invoices — Ledger only ({missingLedgerOnly.length})
                                    </p>
                                    <div className="space-y-1">
                                      {missingLedgerOnly.map(r => (
                                        <MissingRow key={r} invoiceRef={r} stmtAmt={null} ledgerAmt={ledgerAmountMap[r] ?? null} ledgerDate={ledgerDateMap[r]} />
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      );
                    })()}

                    {/* AI summary */}
                    {m.ai_summary && (
                      <p className="mt-2 text-xs text-gray-500 italic border-t border-gray-100 pt-2">{m.ai_summary}</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Tab: Report */}
      {activeTab === 'report' && (
        <div className="space-y-4">
          {/* Assign & Share bar */}
          {session?.status === 'complete' && (
            <div className="flex items-center justify-between gap-3 px-1">
              <p className="text-sm text-gray-500">
                {shareTokens.filter(t => t.is_active).length > 0
                  ? `${shareTokens.filter(t => t.is_active).length} active share link${shareTokens.filter(t => t.is_active).length > 1 ? 's' : ''}`
                  : 'Share this report for external payment approval'}
              </p>
              <button
                onClick={() => {
                  // Compute selectable matches (exclude already-approved) and pre-select all of them
                  const alreadyApproved = new Set();
                  for (const t of shareTokens) {
                    if (!t.is_active || !t.accepted_at) continue;
                    const ids = t.included_match_ids;
                    const scope = Array.isArray(ids) && ids.length > 0
                      ? ids
                      : matches.filter(m => m.review_status === 'accepted').map(m => m.id);
                    scope.forEach(mid => alreadyApproved.add(mid));
                  }
                  const selectable = matches
                    .filter(m => m.review_status === 'accepted' && !alreadyApproved.has(m.id))
                    .map(m => m.id);
                  setSelectedMatchIds(new Set(selectable));
                  setShowShareModal(true);
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 border border-blue-200 hover:bg-blue-50 rounded-lg transition-colors"
              >
                <FiShare2 className="w-3.5 h-3.5" />
                Assign &amp; Share
              </button>
            </div>
          )}

          {loadingReport ? (
            <div className="flex justify-center py-16">
              <FiLoader className="w-8 h-8 animate-spin text-green-600" />
            </div>
          ) : (
            <SupplierReconReport
              reportData={reportData}
              onDownloadDoc={handleMatchDocDownload}
              onPaymentDecision={handlePaymentDecision}
              onSupplierNoteChange={handleSupplierNoteChange}
              onAcceptRecon={handleAcceptRecon}
              acceptingRecon={acceptingRecon}
              matches={matches}
              shareTokens={shareTokens}
              currentUserName={currentUserProfile?.full_name}
            />
          )}

          {/* Audit footer — per-token acceptance status */}
          {shareTokens.filter(t => t.is_active).length > 0 && (() => {
            const activeTokens = shareTokens.filter(t => t.is_active);
            const acceptedTokens = activeTokens.filter(t => t.accepted_at);
            const allAccepted = activeTokens.length > 0 && acceptedTokens.length === activeTokens.length;
            const acceptedMatchSet = new Set(matches.filter(m => m.review_status === 'accepted').map(m => m.id));
            return (
              <div className={`border rounded-xl px-5 py-4 space-y-3 ${allAccepted ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'}`}>
                <div className="flex items-center gap-2">
                  {allAccepted
                    ? <FiCheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    : <FiClock className="w-5 h-5 text-amber-500 flex-shrink-0" />}
                  <p className={`text-sm font-semibold ${allAccepted ? 'text-green-800' : 'text-amber-800'}`}>
                    {allAccepted
                      ? 'All links accepted — Recon approved for payment'
                      : `${acceptedTokens.length} of ${activeTokens.length} link${activeTokens.length !== 1 ? 's' : ''} accepted`}
                  </p>
                </div>
                <div className="space-y-2">
                  {activeTokens.map(t => {
                    const tokenMatchIds = t.included_match_ids;
                    const supplierCount = tokenMatchIds
                      ? matches.filter(m => tokenMatchIds.includes(m.id) && acceptedMatchSet.has(m.id)).length
                      : acceptedMatchSet.size;
                    return (
                      <div key={t.id} className="flex items-center gap-2 text-xs flex-wrap">
                        {t.accepted_at
                          ? <FiCheckCircle className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
                          : <FiClock className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />}
                        <span className={`font-medium ${t.accepted_at ? 'text-green-800' : 'text-amber-700'}`}>
                          {t.assigned_to_name}
                        </span>
                        <span className="text-gray-400">
                          ({supplierCount} supplier{supplierCount !== 1 ? 's' : ''})
                        </span>
                        {t.accepted_at && (
                          <span className="text-green-600">
                            — accepted by {t.accepted_by_name} on{' '}
                            {new Date(t.accepted_at).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' })}
                          </span>
                        )}
                        {!t.accepted_at && (
                          <span className="text-amber-500">— pending</span>
                        )}
                        {t.accepted_at && (
                          <button
                            onClick={() => handleDownloadTokenPaymentCSV(t)}
                            className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-white border border-green-300 text-green-700 rounded hover:bg-green-50 transition-colors flex-shrink-0"
                            title={`Download payment file for ${t.assigned_to_name}`}
                          >
                            <FiDownload className="w-3 h-3" /> Payment file
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* Tab: Accounting */}
      {(activeTab === 'accounting' || accountingOpened) && session?.status === 'complete' && (
        <div hidden={activeTab !== 'accounting'}>
          <SupplierReconAccounting
            key={id}
            sessionId={id}
            groupId={session.group_id}
            canEdit={['admin', 'manager', 'user'].includes(currentUserProfile?.role)}
            active={activeTab === 'accounting'}
            onOpenMatches={() => setActiveTab('matches')}
            onAiRunningChange={setAccountingAiRunning}
          />
        </div>
      )}

      {/* Assign & Share Modal */}
      {showShareModal && (() => {
        const acceptedMatches = matches.filter(m => m.review_status === 'accepted');

        // Build set of match IDs already covered by an accepted token
        const alreadyApprovedIds = new Set();
        const approverByMatchId = {}; // matchId -> assigned_to_name
        for (const t of shareTokens) {
          if (!t.is_active || !t.accepted_at) continue;
          const ids = t.included_match_ids;
          const scope = Array.isArray(ids) && ids.length > 0
            ? ids
            : acceptedMatches.map(m => m.id);
          for (const mid of scope) {
            alreadyApprovedIds.add(mid);
            if (!approverByMatchId[mid]) approverByMatchId[mid] = t.assigned_to_name;
          }
        }

        const selectableMatches = acceptedMatches.filter(m => !alreadyApprovedIds.has(m.id));

        // null means "all selectable" — expand to actual set for display
        const effectiveSelected = selectedMatchIds === null
          ? new Set(selectableMatches.map(m => m.id))
          : new Set([...selectedMatchIds].filter(id => !alreadyApprovedIds.has(id)));

        const allSelectableChecked = selectableMatches.length > 0 &&
          selectableMatches.every(m => effectiveSelected.has(m.id));

        const toggleMatch = (matchId) => {
          if (alreadyApprovedIds.has(matchId)) return;
          setSelectedMatchIds(prev => {
            const base = prev === null ? new Set(selectableMatches.map(m => m.id)) : new Set(prev);
            if (base.has(matchId)) base.delete(matchId);
            else base.add(matchId);
            return base;
          });
        };
        const toggleAll = () => {
          if (allSelectableChecked) setSelectedMatchIds(new Set());
          else setSelectedMatchIds(new Set(selectableMatches.map(m => m.id)));
        };
        return (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6 space-y-5 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-semibold text-gray-900">Assign &amp; Share Report</h3>
                <button onClick={() => setShowShareModal(false)} className="text-gray-400 hover:text-gray-600 text-lg leading-none">✕</button>
              </div>
              <p className="text-sm text-gray-500">
                Generate a unique link for an external user to review payment decisions and accept the recon. No login required.
              </p>

              {/* Create new token */}
              <div className="space-y-3 border border-gray-100 rounded-lg p-4 bg-gray-50">
                <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Create new link</p>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Assigned to (name) *</label>
                  <input
                    type="text"
                    value={shareForm.assigned_to_name}
                    onChange={e => setShareForm(f => ({ ...f, assigned_to_name: e.target.value }))}
                    placeholder="e.g. John Smith"
                    className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Email (optional)</label>
                  <input
                    type="email"
                    value={shareForm.assigned_to_email}
                    onChange={e => setShareForm(f => ({ ...f, assigned_to_email: e.target.value }))}
                    placeholder="john@example.com"
                    className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                </div>

                {/* Supplier selection */}
                {acceptedMatches.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-xs font-medium text-gray-600">
                        Suppliers to include
                        {selectableMatches.length > 0
                          ? ` (${effectiveSelected.size} of ${selectableMatches.length} pending)`
                          : ' — all approved'}
                      </label>
                      {selectableMatches.length > 0 && (
                        <button
                          type="button"
                          onClick={toggleAll}
                          className="text-xs text-blue-600 hover:text-blue-800 underline"
                        >
                          {allSelectableChecked ? 'Deselect all' : 'Select all'}
                        </button>
                      )}
                    </div>
                    <div className="border border-gray-200 rounded-lg bg-white max-h-48 overflow-y-auto divide-y divide-gray-50">
                      {acceptedMatches.map(m => {
                        const approved = alreadyApprovedIds.has(m.id);
                        const approver = approverByMatchId[m.id];
                        return (
                          <label
                            key={m.id}
                            className={`flex items-center gap-2.5 px-3 py-2 select-none ${approved ? 'opacity-50 cursor-not-allowed bg-gray-50' : 'cursor-pointer hover:bg-gray-50'}`}
                          >
                            <input
                              type="checkbox"
                              checked={approved ? false : effectiveSelected.has(m.id)}
                              onChange={() => toggleMatch(m.id)}
                              disabled={approved}
                              className="w-3.5 h-3.5 rounded accent-blue-600 flex-shrink-0"
                            />
                            <span className={`text-sm truncate flex-1 ${approved ? 'text-gray-400 line-through' : 'text-gray-800'}`}>
                              {m.supplier_name}
                            </span>
                            {approved ? (
                              <span className="text-xs text-green-600 flex-shrink-0 font-medium">✓ {approver}</span>
                            ) : (
                              <span className="text-xs text-gray-400 flex-shrink-0">
                                {(() => {
                                  const amt = m.payment_amount != null ? m.payment_amount : m.variance;
                                  return amt != null ? new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(amt) : '';
                                })()}
                              </span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}

                <button
                  onClick={handleCreateShareToken}
                  disabled={!shareForm.assigned_to_name.trim() || shareCreating || effectiveSelected.size === 0}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-40 transition-colors"
                >
                  {shareCreating ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiShare2 className="w-4 h-4" />}
                  Generate Link
                  {effectiveSelected.size > 0 && (
                    <span className="ml-1 text-blue-200">({effectiveSelected.size} supplier{effectiveSelected.size !== 1 ? 's' : ''})</span>
                  )}
                </button>
              </div>

              {/* Existing tokens */}
              {shareTokens.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Shared links</p>
                  {shareTokens.map(t => {
                    const tokenMatchIds = t.included_match_ids;
                    const tokenSuppliers = tokenMatchIds
                      ? acceptedMatches.filter(m => tokenMatchIds.includes(m.id))
                      : acceptedMatches;
                    return (
                      <div key={t.id} className={`border rounded-lg p-3 space-y-1.5 ${t.is_active ? 'border-gray-200 bg-white' : 'border-gray-100 bg-gray-50 opacity-60'}`}>
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-800 truncate">{t.assigned_to_name}</p>
                            {t.assigned_to_email && <p className="text-xs text-gray-400">{t.assigned_to_email}</p>}
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            {t.accepted_at ? (
                              <span className="text-xs text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full font-medium">Accepted</span>
                            ) : t.is_active ? (
                              <>
                                <button
                                  onClick={() => handleCopyLink(t.token)}
                                  className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 border border-blue-200 px-2 py-1 rounded-lg transition-colors"
                                  title="Copy link"
                                >
                                  {copiedToken === t.token ? <FiCheckCircle className="w-3.5 h-3.5" /> : <FiCopy className="w-3.5 h-3.5" />}
                                  {copiedToken === t.token ? 'Copied!' : 'Copy link'}
                                </button>
                                <button
                                  onClick={() => handleRevokeToken(t.id)}
                                  className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
                                  title="Revoke link"
                                >
                                  <FiTrash2 className="w-3.5 h-3.5" />
                                </button>
                              </>
                            ) : (
                              <span className="text-xs text-gray-400">Revoked</span>
                            )}
                          </div>
                        </div>
                        {/* Show which suppliers are in this link */}
                        <p className="text-xs text-gray-400">
                          {tokenMatchIds
                            ? `${tokenSuppliers.length} supplier${tokenSuppliers.length !== 1 ? 's' : ''}: ${tokenSuppliers.map(m => m.supplier_name).join(', ')}`
                            : `All ${acceptedMatches.length} suppliers`}
                        </p>
                        {t.accepted_at && (
                          <p className="text-xs text-green-700">
                            Accepted by <span className="font-medium">{t.accepted_by_name}</span> on{' '}
                            {new Date(t.accepted_at).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' })}
                          </p>
                        )}
                        <p className="text-xs text-gray-400">
                          Created {new Date(t.assigned_at).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </p>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Bank Account Conflict Modal */}
      {bankConflicts.length > 0 && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6 space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <FiAlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900">Bank Account Conflict{bankConflicts.length > 1 ? 's' : ''} Detected</h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  The bank account on the invoice does not match the registered bank account. This could indicate a fraudulent or manipulated invoice.
                  {bankConflicts.length > 1 && ' Decide each supplier independently below.'}
                </p>
              </div>
            </div>

            <div className="space-y-3 max-h-96 overflow-y-auto">
              {bankConflicts.map((c, i) => {
                const decision = conflictDecisions[c.supplier_name] || 'keep';
                return (
                  <div key={i} className="border border-red-200 rounded-lg p-3 bg-red-50 space-y-2">
                    <p className="text-sm font-medium text-gray-900">{c.supplier_name}</p>
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div>
                        <p className="text-gray-500 mb-0.5">Registered bank account</p>
                        <p className="font-mono font-medium text-gray-800">{c.existing_bank_account}</p>
                        {c.existing_bank_name && <p className="text-gray-500">{c.existing_bank_name}</p>}
                      </div>
                      <div>
                        <p className="text-gray-500 mb-0.5">Invoice bank account</p>
                        <p className="font-mono font-medium text-red-700">{c.new_bank_account}</p>
                        {c.new_bank_name && <p className="text-gray-500">{c.new_bank_name}</p>}
                      </div>
                    </div>
                    <div className="flex gap-2 pt-1">
                      <button
                        onClick={() => setConflictDecisions(prev => ({ ...prev, [c.supplier_name]: 'keep' }))}
                        className={`flex-1 px-2.5 py-1.5 text-xs rounded-md border transition-colors ${
                          decision === 'keep'
                            ? 'bg-gray-700 text-white border-gray-700'
                            : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        Keep registered account
                      </button>
                      <button
                        onClick={() => setConflictDecisions(prev => ({ ...prev, [c.supplier_name]: 'accept' }))}
                        className={`flex-1 px-2.5 py-1.5 text-xs rounded-md border transition-colors ${
                          decision === 'accept'
                            ? 'bg-red-600 text-white border-red-600'
                            : 'bg-white text-red-600 border-red-200 hover:bg-red-50'
                        }`}
                      >
                        Accept &amp; update
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <p className="text-xs text-gray-500">
              Only accept a new bank account if you have verified the change is legitimate. Suppliers left on "Keep registered account" will not be modified.
            </p>

            <div className="flex gap-2 justify-end pt-1">
              <button
                onClick={() => handleResolveBankConflicts()}
                className="px-4 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
              >
                Confirm{(() => {
                  const acceptCount = bankConflicts.filter(c => conflictDecisions[c.supplier_name] === 'accept').length;
                  return acceptCount > 0 ? ` (${acceptCount} bank update${acceptCount > 1 ? 's' : ''})` : '';
                })()}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
